// src/components/DocCanvasToolbar.jsx
// UPDATED 2026-08-18 v3: keeps Page Break as a simple toolbar command while
// DocCanvasEditor remains the single authority for hard-break insertion, pagination,
// visible/deletable break markers, history, caret placement, and document reflow.
// Existing image/table bridges, selection preservation, and toolbar behavior are preserved.

import React, { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_TEXT_COLOR = "#111827";
const DOC_WRITER_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif";
const DOC_WRITER_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

const DocCanvasToolbar = (props) => {
  const {
    wrapRef,
    editorRef,

    showToolbar,
    readOnly,

    floatToolbar,
    autoFloat,
    isFloatingNow,
    setFloatToolbar,
    setAutoFloat,
    setIsFloatingNow,

    toolbarMetrics,
    setToolbarMetrics,

    findOpen,
    setFindOpen,
    findQuery,
    setFindQuery,
    findReplace,
    setFindReplace,
    findCaseSensitive,
    setFindCaseSensitive,
    lastFindEnd,
    setLastFindEnd,
    findFixedPos,
    setFindFixedPos,

    isCaretInEditor,

    // Page Break is a single authoritative request:
    // Toolbar -> DocCanvasEditor. The toolbar preserves/restores the caret and
    // never inserts, removes, paginates, or serializes a page-break DOM node itself.
    onInsertPageBreak,

    // Table creation is a single authoritative request:
    // Toolbar -> DocCanvasEditor -> DocWriterTable.
    // The toolbar never inserts or mutates a table DOM node itself.
    insertCanvasTable,

    // Image insertion follows the same single authoritative document-object path:
    // Toolbar -> DocCanvasEditor -> DocWriterImage. The toolbar only chooses the
    // local image file and preserves the caret; the Editor owns placement,
    // persistence, pagination, and the final DocWriterImage mount.
    insertCanvasImage,
    refreshCanvasLayout,

    // Unified document-object history supplied by DocCanvasEditor.
    // DocWriterTable transactions use this path so toolbar Undo/Redo restores
    // the complete table instead of relying on browser DOM history.
    undoEditorTransaction,
    redoEditorTransaction,
    getEditorHistoryState,

    captureEditorSelection,
    restoreEditorSelection,
    beginEditorInteraction,
    endEditorInteraction,
  } = props;

  const toolbarRef = useRef(null);
  const scrollSurfaceRef = useRef(null);
  const findRef = useRef(null);
  const moreButtonRef = useRef(null);
  const moreMenuRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const savedSelectionSnapshotRef = useRef(null);
  const savedSelectionTextRef = useRef("");
  const interactionActiveRef = useRef(false);
  const linkHideTimerRef = useRef(null);
  const colorTypingModeRef = useRef(false);
  const directUndoStackRef = useRef([]);
  const directRedoStackRef = useRef([]);
  // Track whether the latest editable action came from a toolbar/object
  // transaction or from native typing. This lets Undo restore protected
  // chart/table mutations without stealing Undo away from ordinary typing.
  const latestHistoryKindRef = useRef("native");
  const toolbarMutationDepthRef = useRef(0);

  // ===== Formatting state (active buttons) =====
  const [fmt, setFmt] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    ol: false,
    ul: false,
    align: "left",
    block: "p",
    sup: false,
    sub: false,
  });
  const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
  const colorButtonRef = useRef(null);
  const colorMenuRef = useRef(null);
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const [colorMenuPosition, setColorMenuPosition] = useState({ top: 0, left: 0 });
  const [moreOpen, setMoreOpen] = useState(false);
  const [morePosition, setMorePosition] = useState({ top: 0, left: 0 });
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState("3");
  const [tableColumns, setTableColumns] = useState("3");
  const [tableHeaderRow, setTableHeaderRow] = useState(true);
  const [tableError, setTableError] = useState("");
  const tableRowsInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Contextual visual state remains temporarily for the existing chart/math toolbar.
  // Tables no longer mirror toolbar context: DocWriterTable owns its own non-printing
  // Edit button, move handle, resize affordances, and table-specific controls.
  const [canvasObjectContext, setCanvasObjectContext] = useState({
    visualActive: false,
    visualId: "",
    visualType: "visual",
    visualWidth: 0,
    visualHeight: 0,
    visualAlign: "center",
  });
  const [visualDialogOpen, setVisualDialogOpen] = useState(false);
  const [visualSource, setVisualSource] = useState("");
  const [visualWidthInput, setVisualWidthInput] = useState("");
  const [visualHeightInput, setVisualHeightInput] = useState("");
  const [visualError, setVisualError] = useState("");
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkDialogMode, setLinkDialogMode] = useState("create");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState("");
  const [linkSelectedText, setLinkSelectedText] = useState("");
  const linkInputRef = useRef(null);
  const linkEditingAnchorRef = useRef(null);
  const [isCompactToolbar, setIsCompactToolbar] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );

  // ===== Dragging state (toolbar when floating) =====
  const [dragPos, setDragPos] = useState(null); // { top, left } while floating; null = auto-position
  const dragInfoRef = useRef(null); // { startX, startY, startTop, startLeft, dragging, pointerId }
  // rAF throttling for smoother updates
  const rafRef = useRef(0);
  const pendingPosRef = useRef(null);
  const dragPositionRef = useRef(null);
  const closingToolbarRef = useRef(false);

  // Anchored top so it stays near the canvas (not hidden under headers)
  const [floatTop, setFloatTop] = useState(0);

  // ===== Draggable Find panel position =====
  const [findDragPos, setFindDragPos] = useState(null);
  const findDragInfoRef = useRef(null);
  const lastFindMatchRef = useRef(null);

  // ===== Link popover (hover/tap to see & edit URL) =====
  const [linkPopover, setLinkPopover] = useState({
    show: false,
    x: 0,
    y: 0,
    url: "",
    anchor: null,
  });
  const cancelLinkPopoverHide = () => {
    if (linkHideTimerRef.current) {
      window.clearTimeout(linkHideTimerRef.current);
      linkHideTimerRef.current = null;
    }
  };

  const hideLinkPopover = () => {
    cancelLinkPopoverHide();
    setLinkPopover((p) => ({ ...p, show: false, anchor: null }));
  };

  const scheduleLinkPopoverHide = (delay = 320) => {
    cancelLinkPopoverHide();
    linkHideTimerRef.current = window.setTimeout(() => {
      linkHideTimerRef.current = null;
      setLinkPopover((p) => ({ ...p, show: false, anchor: null }));
    }, delay);
  };

  // One-shot suppression so auto-float can't instantly re-pin right after Dock.
  const suppressAutoFloatRef = useRef(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1023px)");
    const apply = () => setIsCompactToolbar(media.matches);
    apply();
    media.addEventListener?.("change", apply);
    return () => media.removeEventListener?.("change", apply);
  }, []);

  // Compact/mobile toolbar always begins at the left edge of its single
  // continuous horizontal scroll surface.
  useEffect(() => {
    if (!isCompactToolbar) return;
    const surface = scrollSurfaceRef.current;
    if (surface) surface.scrollLeft = 0;
  }, [isCompactToolbar]);

  // Give every toolbar control immediate tactile feedback. Pointer-down visibly
  // presses the control, and a short click flash confirms that the action fired.
  useEffect(() => {
    let pressedControl = null;
    const controlSelector = [
      ".doc-canvas-toolbar-root button",
      ".doc-canvas-toolbar-root select",
      ".doc-canvas-toolbar-more-menu button",
      ".doc-canvas-toolbar-dialog button",
      ".doc-canvas-toolbar-find button",
      ".doc-canvas-toolbar-link-popover button",
      ".doc-canvas-toolbar-color-menu button",
    ].join(",");

    const findControl = (target) => {
      try {
        return target?.closest?.(controlSelector) || null;
      } catch {
        return null;
      }
    };

    const releasePressedControl = () => {
      if (pressedControl?.isConnected) {
        pressedControl.removeAttribute("data-toolbar-pressed");
      }
      pressedControl = null;
    };

    const handlePointerDown = (event) => {
      const control = findControl(event.target);
      if (!control || control.disabled) return;
      releasePressedControl();
      pressedControl = control;
      control.setAttribute("data-toolbar-pressed", "true");
    };

    const handleClick = (event) => {
      const control = findControl(event.target);
      if (!control || control.disabled) return;
      control.removeAttribute("data-toolbar-pressed");
      control.setAttribute("data-toolbar-flash", "true");
      if (control.__docToolbarFlashTimer) {
        window.clearTimeout(control.__docToolbarFlashTimer);
      }
      control.__docToolbarFlashTimer = window.setTimeout(() => {
        if (control.isConnected) {
          control.removeAttribute("data-toolbar-flash");
        }
        control.__docToolbarFlashTimer = null;
      }, 170);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("click", handleClick, true);
    window.addEventListener("pointerup", releasePressedControl, true);
    window.addEventListener("pointercancel", releasePressedControl, true);
    window.addEventListener("blur", releasePressedControl);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("click", handleClick, true);
      window.removeEventListener("pointerup", releasePressedControl, true);
      window.removeEventListener("pointercancel", releasePressedControl, true);
      window.removeEventListener("blur", releasePressedControl);
      releasePressedControl();
    };
  }, []);

  // Preserve the exact editor range before any toolbar control receives focus.
  // Every command restores this range so one tap/click always acts at the caret.
  const selectionIsInsideEditor = (selection = window.getSelection()) => {
    try {
      const root = editorRef?.current;
      if (!root || !selection || selection.rangeCount === 0) return false;
      const range = selection.getRangeAt(0);
      const container =
        range.commonAncestorContainer?.nodeType === 1
          ? range.commonAncestorContainer
          : range.commonAncestorContainer?.parentElement;
      return !!container && root.contains(container);
    } catch {
      return false;
    }
  };

  const rememberSelection = () => {
    let captured = false;

    try {
      const snapshot = captureEditorSelection?.();
      if (snapshot) {
        savedSelectionSnapshotRef.current = snapshot;
        captured = true;
      }
    } catch {}

    try {
      const selection = window.getSelection();
      if (!selectionIsInsideEditor(selection)) return captured;
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
      savedSelectionTextRef.current = selection.toString();
      return true;
    } catch {
      return captured;
    }
  };

  const restoreSelection = () => {
    // Prefer the live DOM range first. While the toolbar or an in-app dialog is
    // open, the Editor pauses pagination, so this is the most exact selection
    // and avoids text-offset boundaries resolving into the paragraph above.
    try {
      const root = editorRef?.current;
      const range = savedSelectionRef.current;
      if (
        root &&
        range &&
        range.startContainer?.isConnected &&
        range.endContainer?.isConnected
      ) {
        const container =
          range.commonAncestorContainer?.nodeType === 1
            ? range.commonAncestorContainer
            : range.commonAncestorContainer?.parentElement;
        if (container && root.contains(container)) {
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range.cloneRange());
          return true;
        }
      }
    } catch {}

    // Fall back to the Editor's serializable selection bridge only when the
    // original DOM range is no longer connected.
    try {
      const snapshot = savedSelectionSnapshotRef.current;
      if (snapshot && typeof restoreEditorSelection === "function") {
        const restored = restoreEditorSelection(snapshot);
        if (restored) {
          const selection = window.getSelection();
          if (selectionIsInsideEditor(selection)) {
            savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
            savedSelectionTextRef.current = selection.toString();
          }
          return true;
        }
      }
    } catch {}

    return false;
  };

  const startEditorInteraction = () => {
    rememberSelection();
    if (interactionActiveRef.current) return;
    interactionActiveRef.current = true;
    try { beginEditorInteraction?.(); } catch {}
  };

  const finishEditorInteraction = ({ restore = true } = {}) => {
    if (interactionActiveRef.current) {
      interactionActiveRef.current = false;
      try {
        endEditorInteraction?.({
          restore,
          snapshot: savedSelectionSnapshotRef.current,
        });
      } catch {}
    } else if (restore) {
      restoreSelection();
    }

    if (restore) rememberSelection();
  };

  // ===== Helpers =====
  const focusEditor = () => {
    try {
      editorRef?.current?.focus({ preventScroll: true });
    } catch {
      try { editorRef?.current?.focus(); } catch {}
    }
  };

  const captureScrollPosition = () => {
    const scrollableAncestors = [];
    let node = editorRef?.current?.parentElement || null;

    while (node && node !== document.body) {
      try {
        const computed = window.getComputedStyle(node);
        if (/(auto|scroll)/.test(`${computed.overflowX} ${computed.overflowY}`)) {
          scrollableAncestors.push({ node, left: node.scrollLeft, top: node.scrollTop });
        }
      } catch {}
      node = node.parentElement;
    }

    return { windowX: window.scrollX, windowY: window.scrollY, scrollableAncestors };
  };

  const restoreScrollPosition = (snapshot) => {
    if (!snapshot) return;
    const apply = () => {
      try { window.scrollTo(snapshot.windowX, snapshot.windowY); } catch {}
      snapshot.scrollableAncestors.forEach((item) => {
        try {
          item.node.scrollLeft = item.left;
          item.node.scrollTop = item.top;
        } catch {}
      });
    };
    apply();
    window.requestAnimationFrame(apply);
  };

  const selectionCanvasPage = () => {
    try {
      const root = editorRef?.current;
      const selection = window.getSelection();
      if (!root || !selection || selection.rangeCount === 0) return null;
      let node = selection.anchorNode;
      if (node?.nodeType !== 1) node = node?.parentElement;
      const page = node?.closest?.('[data-doc-canvas-page="true"]');
      return page && root.contains(page) ? page : null;
    } catch {
      return null;
    }
  };

  const keepSelectionInsideCanvasPage = () => {
    const root = editorRef?.current;
    if (!root) return false;
    if (selectionCanvasPage()) return true;

    const pages = Array.from(
      root.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
    );
    const page = pages[pages.length - 1] || pages[0];
    if (!page) return false;

    try {
      const target = page.lastElementChild || page;
      const range = root.ownerDocument.createRange();
      range.selectNodeContents(target);
      range.collapse(false);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    } catch {
      return false;
    }
  };

  const prepareEditorCommand = () => {
    // Formatting commands must run while the contentEditable canvas owns focus.
    // Restore once before focus and again after focus because native controls
    // such as the color picker can temporarily replace the browser selection.
    const restoredBeforeFocus = restoreSelection();
    focusEditor();
    const restoredAfterFocus = restoreSelection();
    const restored = restoredAfterFocus || restoredBeforeFocus;

    // Double safety: no toolbar command may insert or format content on the
    // flex gutter between the Letter-size canvas pages.
    return keepSelectionInsideCanvasPage() && (restored || selectionCanvasPage());
  };

  const captureDirectHistoryState = () => {
    const root = editorRef?.current;
    if (!root) return null;
    return {
      html: root.innerHTML,
      selection: captureEditorSelection?.() || null,
    };
  };

  const beginToolbarMutation = () => {
    toolbarMutationDepthRef.current += 1;
  };

  const endToolbarMutation = () => {
    window.setTimeout(() => {
      toolbarMutationDepthRef.current = Math.max(
        0,
        toolbarMutationDepthRef.current - 1
      );
    }, 0);
  };

  const recordDirectHistory = (before) => {
    const after = captureDirectHistoryState();
    if (!before || !after || before.html === after.html) return false;
    directUndoStackRef.current.push({ before, after });
    if (directUndoStackRef.current.length > 100) {
      directUndoStackRef.current.shift();
    }
    directRedoStackRef.current = [];
    latestHistoryKindRef.current = "direct";
    return true;
  };

  const restoreDirectHistoryState = (state) => {
    const root = editorRef?.current;
    if (!root || !state) return false;
    const scrollSnapshot = captureScrollPosition();

    beginToolbarMutation();
    try {
      root.innerHTML = state.html;
      notifyEditorInput();
      try { refreshCanvasLayout?.(); } catch {}
      if (state.selection && typeof restoreEditorSelection === "function") {
        restoreEditorSelection(state.selection);
      } else {
        focusEditor();
        keepSelectionInsideCanvasPage();
      }
      rememberSelection();
      refreshCanvasObjectContext();
      refreshState();
      restoreScrollPosition(scrollSnapshot);
      window.requestAnimationFrame(() => {
        try { refreshCanvasLayout?.(); } catch {}
        refreshCanvasObjectContext();
        refreshState();
      });
      return true;
    } catch {
      restoreScrollPosition(scrollSnapshot);
      return false;
    } finally {
      endToolbarMutation();
    }
  };

  const runDirectHistoryEntry = (isUndo, entry) => {
    if (!entry) return false;
    const sourceStack = isUndo
      ? directUndoStackRef.current
      : directRedoStackRef.current;
    const destinationStack = isUndo
      ? directRedoStackRef.current
      : directUndoStackRef.current;

    sourceStack.pop();
    const restored = restoreDirectHistoryState(
      isUndo ? entry.before : entry.after
    );
    if (restored) {
      destinationStack.push(entry);
      latestHistoryKindRef.current = "direct";
      return true;
    }

    sourceStack.push(entry);
    return false;
  };

  const runHistoryCommand = (direction) => {
    const root = editorRef?.current;
    if (!root) return false;

    const isUndo = direction === "undo";
    const scrollSnapshot = captureScrollPosition();

    // Document-object transactions (DocWriterTable now; chart/math when the
    // Editor is upgraded) are authoritative in DocCanvasEditor.
    try {
      const editorHistory =
        typeof getEditorHistoryState === "function"
          ? getEditorHistoryState() || {}
          : {};
      const editorLatestKind = String(editorHistory.latestKind || "").toLowerCase();
      const editorCanRun = isUndo
        ? editorHistory.canUndo === true
        : editorHistory.canRedo === true;
      const editorCommand = isUndo ? undoEditorTransaction : redoEditorTransaction;

      if (
        editorCanRun &&
        editorLatestKind &&
        editorLatestKind !== "native" &&
        typeof editorCommand === "function"
      ) {
        const handled = editorCommand();
        if (handled !== false) {
          latestHistoryKindRef.current = "native";
          restoreScrollPosition(scrollSnapshot);
          window.requestAnimationFrame(() => {
            rememberSelection();
            refreshCanvasObjectContext();
            refreshState();
            restoreScrollPosition(scrollSnapshot);
          });
          return true;
        }
      }
    } catch {}

    const directStack = isUndo
      ? directUndoStackRef.current
      : directRedoStackRef.current;
    const directEntry = directStack[directStack.length - 1] || null;

    // Toolbar-owned direct mutations that are not document-object transactions
    // still use the exact before/after snapshots maintained by this toolbar.
    if (directEntry && latestHistoryKindRef.current === "direct") {
      return runDirectHistoryEntry(isUndo, directEntry);
    }

    focusEditor();
    keepSelectionInsideCanvasPage();
    const beforeHtml = root.innerHTML;

    beginToolbarMutation();
    try {
      document.execCommand(direction, false, null);
    } catch {}
    endToolbarMutation();

    const changed = root.innerHTML !== beforeHtml;
    if (changed) {
      notifyEditorInput();
      rememberSelection();
      refreshState();
      restoreScrollPosition(scrollSnapshot);

      if (directEntry) {
        const expected = isUndo ? directEntry.after.html : directEntry.before.html;
        latestHistoryKindRef.current =
          root.innerHTML === expected ? "direct" : "native";
      } else {
        latestHistoryKindRef.current = "native";
      }
      return true;
    }

    if (directEntry) {
      const restored = runDirectHistoryEntry(isUndo, directEntry);
      restoreScrollPosition(scrollSnapshot);
      return restored;
    }

    restoreScrollPosition(scrollSnapshot);
    return false;
  };

  const getClipboardSelection = () => {
    const root = editorRef?.current;
    const selection = window.getSelection();
    if (
      !root ||
      !selectionIsInsideEditor(selection) ||
      selection.rangeCount === 0 ||
      selection.isCollapsed
    ) {
      return null;
    }

    return {
      root,
      selection,
      range: selection.getRangeAt(0).cloneRange(),
    };
  };

  const serializeSelectionForClipboard = (range) => {
    if (!range) return null;

    try {
      const documentValue = editorRef?.current?.ownerDocument || document;
      const container = documentValue.createElement("div");
      container.appendChild(range.cloneContents());

      // The canvas page wrappers are presentation-only. Copy the document
      // content itself so pasting elsewhere never carries Letter-page shells.
      Array.from(
        container.querySelectorAll('[data-doc-canvas-page="true"]')
      ).forEach((page) => {
        page.replaceWith(...Array.from(page.childNodes));
      });

      container.querySelectorAll("*").forEach((element) => {
        Array.from(element.attributes || []).forEach((attribute) => {
          const name = String(attribute.name || "").toLowerCase();
          if (
            name === "contenteditable" ||
            name === "data-page-number" ||
            name.startsWith("data-doc-canvas-")
          ) {
            element.removeAttribute(attribute.name);
          }
        });

        if (element.classList?.contains("doc-canvas-page")) {
          element.classList.remove("doc-canvas-page");
          if (!element.className) element.removeAttribute("class");
        }
      });

      return {
        html: container.innerHTML,
        text: range.toString(),
      };
    } catch {
      return null;
    }
  };

  const writeClipboardThroughNativeCopyEvent = (payload) => {
    if (!payload || (!payload.html && !payload.text)) return false;

    const documentValue = editorRef?.current?.ownerDocument || document;
    let clipboardEventHandled = false;

    const handleCopy = (event) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      try {
        event.preventDefault();
        clipboardData.setData("text/plain", payload.text || "");
        if (payload.html) {
          clipboardData.setData("text/html", payload.html);
        }
        clipboardEventHandled = true;
      } catch {}
    };

    try {
      documentValue.addEventListener("copy", handleCopy, true);
      documentValue.execCommand("copy", false, null);
    } catch {
      clipboardEventHandled = false;
    } finally {
      try {
        documentValue.removeEventListener("copy", handleCopy, true);
      } catch {}
    }

    return clipboardEventHandled;
  };

  const writeClipboardPayload = async (payload) => {
    if (!payload || (!payload.html && !payload.text)) return false;

    try {
      if (
        navigator.clipboard?.write &&
        typeof window.ClipboardItem === "function"
      ) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([payload.html || payload.text || ""], {
            type: "text/html",
          }),
          "text/plain": new Blob([payload.text || ""], {
            type: "text/plain",
          }),
        });
        await navigator.clipboard.write([item]);
        return true;
      }
    } catch {}

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload.text || "");
        return true;
      }
    } catch {}

    return false;
  };

  const readClipboardPayload = async () => {
    try {
      if (navigator.clipboard?.read) {
        const items = await navigator.clipboard.read();
        let html = "";
        let text = "";

        for (const item of items) {
          if (!html && item.types.includes("text/html")) {
            const blob = await item.getType("text/html");
            html = await blob.text();
          }
          if (!text && item.types.includes("text/plain")) {
            const blob = await item.getType("text/plain");
            text = await blob.text();
          }
        }

        if (html || text) return { html, text };
      }
    } catch {}

    try {
      if (navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        if (text) return { html: "", text };
      }
    } catch {}

    return null;
  };

  const sanitizeClipboardHtml = (rawHtml) => {
    const value = String(rawHtml || "");
    if (!value) return "";

    try {
      const documentValue = editorRef?.current?.ownerDocument || document;
      const template = documentValue.createElement("template");
      template.innerHTML = value;
      const fragment = template.content;

      fragment
        .querySelectorAll(
          "script, iframe, object, embed, meta, link, base, form, style"
        )
        .forEach((node) => node.remove());

      fragment.querySelectorAll("*").forEach((element) => {
        Array.from(element.attributes || []).forEach((attribute) => {
          const name = String(attribute.name || "").toLowerCase();
          if (
            name.startsWith("on") ||
            name === "srcdoc" ||
            name === "contenteditable" ||
            name === "data-page-number" ||
            name.startsWith("data-doc-canvas-")
          ) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      Array.from(
        fragment.querySelectorAll('[data-doc-canvas-page="true"], .doc-canvas-page')
      ).forEach((page) => {
        page.replaceWith(...Array.from(page.childNodes));
      });

      return template.innerHTML;
    } catch {
      return "";
    }
  };

  const copySelection = async () => {
    const scrollSnapshot = captureScrollPosition();
    const startedHere = !interactionActiveRef.current;
    if (startedHere) startEditorInteraction();

    if (!prepareEditorCommand()) {
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return false;
    }

    const selected = getClipboardSelection();
    if (!selected) {
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return false;
    }

    const payload = serializeSelectionForClipboard(selected.range);

    // Populate the operating-system clipboard from the trusted button click.
    // This path avoids a Clipboard API permission prompt and lets the content
    // paste into external programs such as Microsoft Word.
    let copied = writeClipboardThroughNativeCopyEvent(payload);

    // Clipboard API remains a fallback for browsers that do not dispatch a
    // writable native copy event from execCommand.
    if (!copied) copied = await writeClipboardPayload(payload);

    restoreSelection();
    rememberSelection();
    refreshState();
    restoreScrollPosition(scrollSnapshot);
    if (startedHere) finishEditorInteraction({ restore: true });
    return copied;
  };

  const cutSelection = async () => {
    const root = editorRef?.current;
    if (!root) return false;

    const scrollSnapshot = captureScrollPosition();
    const startedHere = !interactionActiveRef.current;
    if (startedHere) startEditorInteraction();

    if (!prepareEditorCommand()) {
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return false;
    }

    const selected = getClipboardSelection();
    if (!selected) {
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return false;
    }

    const historyBefore = captureDirectHistoryState();
    const beforeHtml = root.innerHTML;
    const payload = serializeSelectionForClipboard(selected.range);

    // Copy to the operating-system clipboard first. Delete only after the copy
    // succeeds so Cut never removes content that was not actually copied.
    let copied = writeClipboardThroughNativeCopyEvent(payload);
    if (!copied) copied = await writeClipboardPayload(payload);

    let changed = false;
    if (copied) {
      restoreSelection();
      focusEditor();
      restoreSelection();

      try {
        document.execCommand("delete", false, null);
        changed = root.innerHTML !== beforeHtml;
      } catch {}

      if (!changed) {
        try {
          const liveSelection = window.getSelection();
          if (
            selectionIsInsideEditor(liveSelection) &&
            liveSelection.rangeCount > 0 &&
            !liveSelection.isCollapsed
          ) {
            const range = liveSelection.getRangeAt(0);
            range.deleteContents();
            range.collapse(true);
            liveSelection.removeAllRanges();
            liveSelection.addRange(range);
            changed = root.innerHTML !== beforeHtml;
          }
        } catch {}
      }
    }

    if (changed) {
      recordDirectHistory(historyBefore);
      notifyEditorInput();
      keepSelectionInsideCanvasPage();
      rememberSelection();
      refreshState();
    }

    restoreScrollPosition(scrollSnapshot);
    if (startedHere) finishEditorInteraction({ restore: false });
    return changed;
  };

  const pasteClipboard = async () => {
    const root = editorRef?.current;
    if (!root) return false;

    const scrollSnapshot = captureScrollPosition();
    const startedHere = !interactionActiveRef.current;
    if (startedHere) startEditorInteraction();

    if (!prepareEditorCommand()) {
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return false;
    }

    const historyBefore = captureDirectHistoryState();
    const beforeHtml = root.innerHTML;
    let changed = false;

    // Some browsers still permit the native command for an explicit toolbar
    // click. Use it first because it preserves rich clipboard formatting.
    try {
      document.execCommand("paste", false, null);
      changed = root.innerHTML !== beforeHtml;
    } catch {}

    if (!changed) {
      const payload = await readClipboardPayload();
      if (payload) {
        restoreSelection();
        focusEditor();
        restoreSelection();
        keepSelectionInsideCanvasPage();

        const html = sanitizeClipboardHtml(payload.html);
        try {
          if (html) {
            document.execCommand("insertHTML", false, html);
          } else {
            document.execCommand("insertText", false, payload.text || "");
          }
          changed = root.innerHTML !== beforeHtml;
        } catch {}
      }
    }

    if (changed) {
      notifyEditorInput();
      keepSelectionInsideCanvasPage();
      rememberSelection();
      refreshState();
      recordDirectHistory(historyBefore);
    }

    restoreScrollPosition(scrollSnapshot);
    if (startedHere) finishEditorInteraction({ restore: false });
    return changed;
  };

  const selectAllEditorContent = () => {
    const root = editorRef?.current;
    if (!root) return false;
    const scrollSnapshot = captureScrollPosition();

    try {
      focusEditor();
      const range = root.ownerDocument.createRange();
      range.selectNodeContents(root);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      rememberSelection();
      refreshState();
      restoreScrollPosition(scrollSnapshot);
      return true;
    } catch {
      restoreScrollPosition(scrollSnapshot);
      return false;
    }
  };

  const PRIMARY_EDITABLE_BLOCK_SELECTOR =
    "p,h1,h2,h3,h4,h5,h6,blockquote,li,pre,td,th,address";
  const STRUCTURAL_EDITABLE_BLOCK_SELECTOR =
    "section,article,header,footer,aside,div:not([data-doc-canvas-page])";
  const EDITABLE_BLOCK_SELECTOR = `${PRIMARY_EDITABLE_BLOCK_SELECTOR},${STRUCTURAL_EDITABLE_BLOCK_SELECTOR}`;

  const closestEditableBlock = (node) => {
    const root = editorRef?.current;
    if (!root || !node) return null;

    let element = node.nodeType === 1 ? node : node.parentElement;
    let structuralFallback = null;

    while (element && element !== root) {
      if (element.getAttribute?.("data-doc-canvas-page") === "true") break;
      if (element.matches?.(PRIMARY_EDITABLE_BLOCK_SELECTOR)) return element;
      if (
        !structuralFallback &&
        element.matches?.(STRUCTURAL_EDITABLE_BLOCK_SELECTOR)
      ) {
        structuralFallback = element;
      }
      element = element.parentElement;
    }

    return structuralFallback;
  };

  const currentEditorRange = () => {
    try {
      const selection = window.getSelection();
      if (selectionIsInsideEditor(selection)) return selection.getRangeAt(0);
      const saved = savedSelectionRef.current;
      const root = editorRef?.current;
      if (!saved || !root || !saved.startContainer?.isConnected || !saved.endContainer?.isConnected) return null;
      const container = saved.commonAncestorContainer?.nodeType === 1 ? saved.commonAncestorContainer : saved.commonAncestorContainer?.parentElement;
      return container && root.contains(container) ? saved : null;
    } catch {
      return null;
    }
  };

  const paragraphTargets = () => {
    const root = editorRef?.current;
    const range = currentEditorRange();
    if (!root || !range) return [];

    if (range.collapsed) {
      const block = closestEditableBlock(range.startContainer);
      return block ? [block] : [];
    }

    const matches = Array.from(
      root.querySelectorAll(EDITABLE_BLOCK_SELECTOR)
    ).filter((block) => {
      try {
        const blockRange = root.ownerDocument.createRange();
        blockRange.selectNodeContents(block);

        // A selection that merely touches a block boundary must not format the
        // previous or next paragraph. Require a real content intersection.
        const endsAfterBlockStarts =
          range.compareBoundaryPoints(Range.END_TO_START, blockRange) > 0;
        const startsBeforeBlockEnds =
          range.compareBoundaryPoints(Range.START_TO_END, blockRange) < 0;
        return endsAfterBlockStarts && startsBeforeBlockEnds;
      } catch {
        return false;
      }
    });

    return matches.filter(
      (block) =>
        !matches.some(
          (other) => other !== block && block.contains(other)
        )
    );
  };

  const applyParagraphMutation = (mutate) => {
    const scrollSnapshot = captureScrollPosition();
    const historyBefore = captureDirectHistoryState();
    prepareEditorCommand();
    const blocks = paragraphTargets();
    if (!blocks.length) {
      restoreScrollPosition(scrollSnapshot);
      return false;
    }
    blocks.forEach((block) => mutate(block));
    notifyEditorInput();
    rememberSelection();
    refreshState();
    recordDirectHistory(historyBefore);
    restoreScrollPosition(scrollSnapshot);
    return true;
  };

  const normalizeUrl = (raw) => {
    const value = String(raw || "").trim();
    if (!value) return "";

    if (/^(mailto:|tel:)/i.test(value)) return value;

    const candidate = /^(https?:)?\/\//i.test(value)
      ? value.startsWith("//")
        ? `https:${value}`
        : value
      : `https://${value.replace(/^\/*/, "")}`;

    try {
      const parsed = new URL(candidate);
      if (!["http:", "https:"].includes(parsed.protocol)) return "";
      return parsed.href;
    } catch {
      return "";
    }
  };

  const exec = (cmd, val = null) => {
    const scrollSnapshot = captureScrollPosition();
    prepareEditorCommand();
    document.execCommand(cmd, false, val);
    notifyEditorInput();
    rememberSelection();
    refreshState();
    restoreScrollPosition(scrollSnapshot);
    window.requestAnimationFrame(() => {
      rememberSelection();
      refreshState();
    });
  };

  const enforceAnchorUX = () => {
    try {
      const root = editorRef?.current;
      if (!root) return;
      root.querySelectorAll("a").forEach((anchor) => {
        const href = anchor.getAttribute("href") || "";
        if (anchor.getAttribute("target") !== "_blank") anchor.setAttribute("target", "_blank");
        if (anchor.getAttribute("rel") !== "noopener noreferrer") anchor.setAttribute("rel", "noopener noreferrer");
        if (anchor.getAttribute("title") !== href) anchor.setAttribute("title", href);
        if (anchor.style.cursor !== "pointer") anchor.style.cursor = "pointer";
      });
    } catch {}
  };

  const afterCreateLinkSetAttrs = () => {
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      let node = sel.anchorNode;
      while (node && node.nodeType === 3) node = node.parentNode;
      const a =
        node && node.closest ? node.closest("a") : (node && node.tagName === "A" ? node : null);
      if (a) {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
        a.setAttribute("title", a.getAttribute("href") || "");
        a.removeAttribute("contenteditable");
        a.style.cursor = "pointer";
      }
    } catch {}
  };

  const openCreateLinkDialog = () => {
    startEditorInteraction();
    restoreSelection();
    linkEditingAnchorRef.current = null;
    setLinkDialogMode("create");
    setLinkSelectedText(savedSelectionTextRef.current || "");
    setLinkUrl("");
    setLinkError("");
    setLinkDialogOpen(true);
    window.setTimeout(() => {
      linkInputRef.current?.focus();
    }, 0);
  };

  const openEditLinkDialog = (anchor) => {
    if (!anchor) return;
    startEditorInteraction();
    linkEditingAnchorRef.current = anchor;
    setLinkDialogMode("edit");
    setLinkSelectedText(String(anchor.textContent || ""));
    setLinkUrl(anchor.getAttribute("href") || "");
    setLinkError("");
    setLinkDialogOpen(true);
    hideLinkPopover();
    window.setTimeout(() => {
      linkInputRef.current?.focus();
      linkInputRef.current?.select?.();
    }, 0);
  };

  const closeLinkDialog = () => {
    setLinkDialogOpen(false);
    setLinkError("");
    setLinkSelectedText("");
    linkEditingAnchorRef.current = null;
    window.requestAnimationFrame(() => {
      finishEditorInteraction({ restore: true });
      refreshState();
    });
  };

  const setAnchorAttributes = (anchor, url) => {
    if (!anchor) return;
    anchor.setAttribute("href", url);
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
    anchor.setAttribute("title", url);
    anchor.style.cursor = "pointer";
  };

  const closestAnchor = (node) => {
    const root = editorRef?.current;
    let element = node?.nodeType === 1 ? node : node?.parentElement;
    while (element && element !== root) {
      if (element.tagName === "A") return element;
      element = element.parentElement;
    }
    return null;
  };

  const applyLinkAtSelection = (url) => {
    const root = editorRef?.current;
    const selection = window.getSelection();
    if (!root || !selectionIsInsideEditor(selection)) return null;

    const range = selection.getRangeAt(0).cloneRange();
    const documentValue = root.ownerDocument;
    const existingStartAnchor = closestAnchor(range.startContainer);
    const existingEndAnchor = closestAnchor(range.endContainer);

    if (
      existingStartAnchor &&
      existingStartAnchor === existingEndAnchor &&
      root.contains(existingStartAnchor)
    ) {
      setAnchorAttributes(existingStartAnchor, url);
      return { anchor: existingStartAnchor, directMutation: true };
    }

    if (range.collapsed) {
      const anchor = documentValue.createElement("a");
      setAnchorAttributes(anchor, url);
      anchor.textContent = url;
      range.insertNode(anchor);

      const caret = documentValue.createRange();
      caret.setStartAfter(anchor);
      caret.collapse(true);
      selection.removeAllRanges();
      selection.addRange(caret);
      return { anchor, directMutation: true };
    }

    const selectedText = selection.toString();
    const startBlock = closestEditableBlock(range.startContainer);
    const endBlock = closestEditableBlock(range.endContainer);

    // For the normal word/sentence case, wrap the exact selected DOM contents
    // ourselves. This is deterministic and keeps the visible text in place.
    if (startBlock && startBlock === endBlock) {
      try {
        const anchor = documentValue.createElement("a");
        setAnchorAttributes(anchor, url);
        anchor.appendChild(range.extractContents());
        range.insertNode(anchor);

        const linkedRange = documentValue.createRange();
        linkedRange.selectNodeContents(anchor);
        selection.removeAllRanges();
        selection.addRange(linkedRange);
        return { anchor, directMutation: true };
      } catch {}
    }

    // Fallback for a selection spanning multiple editable blocks. Detect the
    // actual anchor created by the browser instead of reporting an error after
    // the link has already been added.
    const anchorsBefore = new Set(root.querySelectorAll("a"));
    document.execCommand("createLink", false, url);
    enforceAnchorUX();

    const anchorsAfter = Array.from(root.querySelectorAll("a"));
    const anchor =
      anchorsAfter.find((candidate) => !anchorsBefore.has(candidate)) ||
      anchorsAfter.find(
        (candidate) =>
          candidate.getAttribute("href") === url &&
          (!selectedText || String(candidate.textContent || "") === selectedText)
      ) ||
      closestAnchor(selection.anchorNode) ||
      null;

    return anchor ? { anchor, directMutation: false } : null;
  };

  const submitLinkDialog = (event) => {
    event?.preventDefault?.();
    const raw = String(linkUrl || "").trim();
    if (!raw) {
      setLinkError("Enter a link address.");
      return;
    }

    const url = normalizeUrl(raw);
    if (!url) {
      setLinkError("Enter a valid web address, such as www.example.com or https://example.com.");
      return;
    }

    const historyBefore = captureDirectHistoryState();
    let directMutation = false;

    if (linkDialogMode === "edit") {
      const anchor = linkEditingAnchorRef.current;
      if (!anchor || !anchor.isConnected || !editorRef?.current?.contains(anchor)) {
        setLinkError("This link is no longer available. Select it again and retry.");
        return;
      }
      setAnchorAttributes(anchor, url);
      directMutation = true;
      notifyEditorInput();
      enforceAnchorUX();
    } else {
      const scrollSnapshot = captureScrollPosition();
      if (!prepareEditorCommand()) {
        setLinkError("Select text in the document or place the cursor where the link should be inserted.");
        return;
      }

      let linkResult = applyLinkAtSelection(url);
      let anchor = linkResult?.anchor || null;
      directMutation = Boolean(linkResult?.directMutation);
      if (!anchor) {
        anchor = Array.from(editorRef?.current?.querySelectorAll("a") || []).find(
          (candidate) =>
            candidate.getAttribute("href") === url &&
            (!linkSelectedText ||
              String(candidate.textContent || "") === linkSelectedText)
        );
      }
      if (!anchor) {
        setLinkError("The selected text could not be linked. Select it again and retry.");
        return;
      }

      notifyEditorInput();
      enforceAnchorUX();
      rememberSelection();
      refreshState();
      restoreScrollPosition(scrollSnapshot);
    }

    if (directMutation) recordDirectHistory(historyBefore);

    setLinkDialogOpen(false);
    setLinkError("");
    setLinkSelectedText("");
    linkEditingAnchorRef.current = null;
    window.requestAnimationFrame(() => {
      finishEditorInteraction({ restore: true });
      refreshState();
    });
  };

  const unlink = () => exec("unlink");

  const setBlock = (blockName) => {
    // Extended to include all H1–H6 as requested
    const map = {
      p: "P",
      h1: "H1",
      h2: "H2",
      h3: "H3",
      h4: "H4",
      h5: "H5",
      h6: "H6",
    };
    exec("formatBlock", map[blockName] || "P");
  };

  const addQuotationMarks = () => {
    const startedHere = !interactionActiveRef.current;
    if (startedHere) startEditorInteraction();

    const scrollSnapshot = captureScrollPosition();
    if (!prepareEditorCommand()) {
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return false;
    }

    const root = editorRef?.current;
    const selection = window.getSelection();
    if (!root || !selectionIsInsideEditor(selection)) {
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return false;
    }

    try {
      let range = selection.getRangeAt(0).cloneRange();
      const wasCollapsed = range.collapsed;
      const historyBefore = wasCollapsed ? null : captureDirectHistoryState();
      const documentValue = root.ownerDocument;

      if (range.collapsed && !closestEditableBlock(range.startContainer)) {
        // A browser can occasionally leave a collapsed selection on the outer
        // paper/page wrapper. Create a normal paragraph at that visual position
        // so quotation marks can never be inserted into the surrounding gutter.
        let page =
          range.startContainer?.nodeType === 1 &&
          range.startContainer.getAttribute?.("data-doc-canvas-page") === "true"
            ? range.startContainer
            : range.startContainer?.parentElement?.closest?.(
                '[data-doc-canvas-page="true"]'
              );

        if (!page && range.startContainer === root) {
          const candidate =
            root.childNodes[Math.min(range.startOffset, root.childNodes.length - 1)] ||
            root.lastChild;
          page =
            candidate?.nodeType === 1 &&
            candidate.getAttribute?.("data-doc-canvas-page") === "true"
              ? candidate
              : root.querySelector('[data-doc-canvas-page="true"]');
        }

        if (page) {
          const paragraph = documentValue.createElement("p");
          paragraph.appendChild(documentValue.createElement("br"));

          if (range.startContainer === page && page.childNodes[range.startOffset]) {
            page.insertBefore(paragraph, page.childNodes[range.startOffset]);
          } else {
            page.appendChild(paragraph);
          }

          range = documentValue.createRange();
          range.setStart(paragraph, 0);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }

      if (range.collapsed) {
        // Native insertion keeps the quotation marks inside the active paragraph
        // instead of creating a text node beside the Letter-size page wrapper.
        document.execCommand("insertText", false, "“”");
        try {
          window.getSelection()?.modify?.("move", "backward", "character");
        } catch {}
      } else {
        // Insert the closing mark first, then the opening mark. This preserves
        // all formatting inside the selected text and never moves it to the gutter.
        const closingMark = documentValue.createTextNode("”");
        const closingRange = range.cloneRange();
        closingRange.collapse(false);
        closingRange.insertNode(closingMark);

        const openingMark = documentValue.createTextNode("“");
        const openingRange = range.cloneRange();
        openingRange.collapse(true);
        openingRange.insertNode(openingMark);

        const quotedRange = documentValue.createRange();
        quotedRange.setStartAfter(openingMark);
        quotedRange.setEndBefore(closingMark);
        selection.removeAllRanges();
        selection.addRange(quotedRange);
      }

      notifyEditorInput();
      rememberSelection();
      refreshState();
      if (historyBefore) recordDirectHistory(historyBefore);
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return true;
    } catch {
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return false;
    }
  };

  const setAlign = (dir) => {
    const normalized = ["left", "center", "right"].includes(dir) ? dir : "left";
    const changed = applyParagraphMutation((block) => {
      block.style.setProperty("text-align", normalized, "important");
    });
    if (changed) setFmt((current) => ({ ...current, align: normalized }));
  };

  const adjustIndent = (amount) => {
    applyParagraphMutation((block) => {
      const inlineMargin = Number.parseFloat(
        block.style.getPropertyValue("margin-left") || ""
      );
      const computedMargin = Number.parseFloat(
        window.getComputedStyle(block).marginLeft || "0"
      );
      const currentMargin = Number.isFinite(inlineMargin)
        ? inlineMargin
        : Number.isFinite(computedMargin)
        ? computedMargin
        : 0;
      const nextMargin = Math.max(0, Math.min(288, currentMargin + amount));

      if (nextMargin > 0) {
        block.style.setProperty("margin-left", `${nextMargin}px`, "important");
      } else {
        block.style.removeProperty("margin-left");
      }
    });
  };

  const toggleStrike = () => exec("strikeThrough");
  const toggleSup = () => exec("superscript");
  const toggleSub = () => exec("subscript");

  const highlight = () => {
    const scrollSnapshot = captureScrollPosition();
    prepareEditorCommand();
    try {
      document.execCommand("hiliteColor", false, "#fff59d");
    } catch {
      document.execCommand("backColor", false, "#fff59d");
    }
    notifyEditorInput();
    rememberSelection();
    refreshState();
    restoreScrollPosition(scrollSnapshot);
  };

  const applyTextColor = (color) => {
    const nextColor = String(color || DEFAULT_TEXT_COLOR);
    const startedHere = !interactionActiveRef.current;
    if (startedHere) startEditorInteraction();

    const scrollSnapshot = captureScrollPosition();
    if (!prepareEditorCommand()) {
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return { applied: false, typingMode: false };
    }

    try {
      const root = editorRef?.current;
      const selection = window.getSelection();
      const typingMode = Boolean(
        root &&
        selectionIsInsideEditor(selection) &&
        selection.isCollapsed
      );

      // Store the chosen color on the editor itself. DocCanvasEditor reapplies
      // it during beforeinput, so a collapsed caret keeps typing in this color
      // even after the native color picker has temporarily taken focus.
      if (root) root.setAttribute("data-doc-typing-color", nextColor);
      colorTypingModeRef.current = typingMode;

      document.execCommand("styleWithCSS", false, true);
      const applied = document.execCommand("foreColor", false, nextColor);
      notifyEditorInput();
      setTextColor(nextColor);
      rememberSelection();
      refreshState();
      restoreScrollPosition(scrollSnapshot);

      // Restoring a brand-new Range after a collapsed foreColor command clears
      // the browser's pending typing style. Keep the live caret untouched.
      if (startedHere) {
        finishEditorInteraction({ restore: !typingMode });
      }

      return { applied: applied !== false, typingMode };
    } catch {
      colorTypingModeRef.current = false;
      restoreScrollPosition(scrollSnapshot);
      if (startedHere) finishEditorInteraction({ restore: true });
      return { applied: false, typingMode: false };
    }
  };

  const computeColorMenuPosition = () => {
    const button = colorButtonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const menuWidth = 236;
    const estimatedHeight = 150;
    const left = Math.max(
      8,
      Math.min(window.innerWidth - menuWidth - 8, rect.left)
    );
    const belowTop = rect.bottom + 8;
    const top =
      belowTop + estimatedHeight <= window.innerHeight - 8
        ? belowTop
        : Math.max(8, rect.top - estimatedHeight - 8);

    setColorMenuPosition({ top, left });
  };

  const closeColorMenu = ({ restore = true } = {}) => {
    setColorMenuOpen(false);
    window.requestAnimationFrame(() => {
      finishEditorInteraction({ restore });
      refreshState();
    });
  };

  const toggleColorMenu = () => {
    if (colorMenuOpen) {
      closeColorMenu({ restore: true });
      return;
    }

    setMoreOpen(false);
    startEditorInteraction();
    restoreSelection();
    setColorMenuOpen(true);
    window.requestAnimationFrame(computeColorMenuPosition);
  };

  const chooseTextColor = (color) => {
    const result = applyTextColor(color);
    if (!result?.applied) return;

    // Keep the in-app color menu mounted while the browser's native color
    // picker is open. Unmounting it during an input/change event immediately
    // closes the picker and prevents the user from dragging through colors.
    setColorMenuOpen(true);
    window.requestAnimationFrame(refreshState);
  };

  const resetTextColor = () => {
    chooseTextColor(DEFAULT_TEXT_COLOR);
  };

  useEffect(() => {
    if (!colorMenuOpen) return undefined;

    const closeOnOutsidePress = (event) => {
      const target = event.target;
      if (
        colorMenuRef.current?.contains(target) ||
        colorButtonRef.current?.contains(target)
      ) {
        return;
      }
      closeColorMenu({ restore: true });
    };

    const reposition = () => computeColorMenuPosition();
    computeColorMenuPosition();
    document.addEventListener("pointerdown", closeOnOutsidePress);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePress);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [colorMenuOpen, isFloatingNow, dragPos]);

  const notifyEditorInput = () => {
    try {
      editorRef?.current?.dispatchEvent(
        new Event("input", { bubbles: true })
      );
    } catch {}
  };


  const escapeCanvasSelectorValue = (value) => {
    const text = String(value || "");
    try {
      if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        return CSS.escape(text);
      }
    } catch {}
    return text.replace(/["\\]/g, "\\$&");
  };

  const selectionElement = () => {
    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return null;
      const node = selection.anchorNode;
      return node?.nodeType === 1 ? node : node?.parentElement || null;
    } catch {
      return null;
    }
  };

  const selectedVisualBlock = () => {
    const root = editorRef?.current;
    if (!root) return null;

    const explicitlySelected = root.querySelector(
      '[data-doc-visual-selected="true"]'
    );
    if (explicitlySelected) return explicitlySelected;

    const element = selectionElement();
    const closest = element?.closest?.(
      '[data-doc-protected-block="true"], .doc-canvas-visual-block, .answer-chart-card, .answer-display-math, .answer-math-block'
    );
    return closest && root.contains(closest) ? closest : null;
  };

  const inferHorizontalAlignment = (element) => {
    if (!element) return "center";
    const left = String(element.style.marginLeft || "").toLowerCase();
    const right = String(element.style.marginRight || "").toLowerCase();
    if (left === "auto" && right === "auto") return "center";
    if (left === "auto") return "right";
    return "left";
  };

  const refreshCanvasObjectContext = () => {
    const visual = selectedVisualBlock();
    const visualRect = visual?.getBoundingClientRect?.();
    const visualType = String(
      visual?.getAttribute("data-doc-block-type") ||
        visual?.getAttribute("data-doc-visual-type") ||
        "visual"
    );

    setCanvasObjectContext({
      visualActive: Boolean(visual),
      visualId: visual?.getAttribute("data-doc-block-id") || "",
      visualType,
      visualWidth: visualRect ? Math.round(visualRect.width) : 0,
      visualHeight: visualRect ? Math.round(visualRect.height) : 0,
      visualAlign: inferHorizontalAlignment(visual),
    });
  };

  useEffect(() => {
    const root = editorRef?.current;
    if (!root) return undefined;

    const refresh = () => window.requestAnimationFrame(refreshCanvasObjectContext);
    refresh();
    document.addEventListener("selectionchange", refresh);
    root.addEventListener("pointerup", refresh);
    root.addEventListener("keyup", refresh);
    root.addEventListener("input", refresh);

    return () => {
      document.removeEventListener("selectionchange", refresh);
      root.removeEventListener("pointerup", refresh);
      root.removeEventListener("keyup", refresh);
      root.removeEventListener("input", refresh);
    };
  }, [editorRef]);

  const commitCanvasObjectMutation = (mutate) => {
    const root = editorRef?.current;
    if (!root || typeof mutate !== "function") return false;
    const historyBefore = captureDirectHistoryState();

    beginToolbarMutation();
    let changed = false;
    try {
      changed = mutate() !== false;
    } finally {
      endToolbarMutation();
    }
    if (!changed) return false;

    recordDirectHistory(historyBefore);
    notifyEditorInput();
    try { refreshCanvasLayout?.(); } catch {}
    window.requestAnimationFrame(() => {
      refreshCanvasObjectContext();
      refreshState();
    });
    return true;
  };

  // Table editing commands now live inside DocWriterTable. The main toolbar
  // intentionally has no row/column/split/merge/resize/delete table controls.

  const resizeSelectedVisual = (scale) =>
    commitCanvasObjectMutation(() => {
      const visual = selectedVisualBlock();
      if (!visual) return false;
      const rect = visual.getBoundingClientRect();
      const page = visual.closest?.('[data-doc-canvas-page="true"]');
      const pageStyle = page ? window.getComputedStyle(page) : null;
      const maxWidth = page
        ? Math.max(
            140,
            page.clientWidth -
              (Number.parseFloat(pageStyle?.paddingLeft || "0") || 0) -
              (Number.parseFloat(pageStyle?.paddingRight || "0") || 0)
          )
        : rect.width * 1.5;
      visual.style.width = `${Math.round(
        Math.max(140, Math.min(maxWidth, rect.width * scale))
      )}px`;
      visual.style.height = `${Math.round(
        Math.max(80, Math.min(760, rect.height * scale))
      )}px`;
      visual.style.maxWidth = "100%";
      return true;
    });

  const setSelectedVisualFullWidth = () =>
    commitCanvasObjectMutation(() => {
      const visual = selectedVisualBlock();
      if (!visual) return false;
      visual.style.width = "100%";
      visual.style.maxWidth = "100%";
      visual.style.height = "auto";
      return true;
    });

  const setSelectedVisualAlignment = (alignment) =>
    commitCanvasObjectMutation(() => {
      const visual = selectedVisualBlock();
      if (!visual) return false;
      visual.style.display = "block";
      if (alignment === "center") {
        visual.style.marginLeft = "auto";
        visual.style.marginRight = "auto";
      } else if (alignment === "right") {
        visual.style.marginLeft = "auto";
        visual.style.marginRight = "0";
      } else {
        visual.style.marginLeft = "0";
        visual.style.marginRight = "auto";
      }
      return true;
    });

  const deleteSelectedVisual = () =>
    commitCanvasObjectMutation(() => {
      const visual = selectedVisualBlock();
      if (!visual) return false;
      visual.remove();
      return true;
    });

  const openVisualDialog = () => {
    const visual = selectedVisualBlock();
    if (!visual) return;
    startEditorInteraction();
    setVisualSource(visual.getAttribute("data-doc-block-source") || "");
    const rect = visual.getBoundingClientRect();
    setVisualWidthInput(String(Math.round(rect.width)));
    setVisualHeightInput(String(Math.round(rect.height)));
    setVisualError("");
    setVisualDialogOpen(true);
  };

  const closeVisualDialog = () => {
    setVisualDialogOpen(false);
    setVisualError("");
    window.requestAnimationFrame(() => finishEditorInteraction({ restore: false }));
  };

  const submitVisualDialog = (event) => {
    event?.preventDefault?.();
    const visual = selectedVisualBlock();
    if (!visual) {
      setVisualError("Select the chart or math block again.");
      return;
    }

    const width = Number.parseInt(visualWidthInput, 10);
    const height = Number.parseInt(visualHeightInput, 10);
    if (!Number.isFinite(width) || width < 140 || width > 1600) {
      setVisualError("Width must be between 140 and 1600 pixels.");
      return;
    }
    if (!Number.isFinite(height) || height < 80 || height > 1200) {
      setVisualError("Height must be between 80 and 1200 pixels.");
      return;
    }

    const blockId = visual.getAttribute("data-doc-block-id") || "";
    const blockType = visual.getAttribute("data-doc-block-type") || "visual";
    commitCanvasObjectMutation(() => {
      visual.style.width = `${width}px`;
      visual.style.height = `${height}px`;
      visual.style.maxWidth = "100%";
      visual.setAttribute("data-doc-block-source", visualSource);
      return true;
    });

    try {
      editorRef.current?.dispatchEvent(
        new CustomEvent("doc-canvas-visual-edit", {
          bubbles: true,
          detail: {
            blockId,
            type: blockType,
            source: visualSource,
            width,
            height,
          },
        })
      );
    } catch {}

    setVisualDialogOpen(false);
    setVisualError("");
    window.requestAnimationFrame(() => finishEditorInteraction({ restore: false }));
  };

  const insertPageBreak = () => {
    const scrollSnapshot = captureScrollPosition();

    // The toolbar owns only the user command and caret preservation. The Editor
    // owns the hard-break object itself, the following page, cascading pagination,
    // Undo/Redo history, and the visible non-printing Page Break marker.
    if (!prepareEditorCommand()) {
      restoreScrollPosition(scrollSnapshot);
      return false;
    }

    if (typeof onInsertPageBreak !== "function") {
      restoreScrollPosition(scrollSnapshot);
      return false;
    }

    let handled = false;
    try {
      handled = onInsertPageBreak() !== false;
    } catch {
      handled = false;
    }

    restoreScrollPosition(scrollSnapshot);

    // DocCanvasEditor restores the caret after the break on the newly active page.
    // Capture that post-command caret for the next toolbar action; do not restore
    // the pre-break range here or it would jump the user back across the hard break.
    window.requestAnimationFrame(() => {
      if (handled) focusEditor();
      rememberSelection();
      refreshState();
      restoreScrollPosition(scrollSnapshot);
    });

    return handled;
  };

  const toggleFindPanel = () => {
    setFindOpen((current) => !current);
    if (!findOpen) setFindDragPos(null);
    setTimeout(computeFloating, 0);
  };

  const floatToolbarOnly = () => {
    // A floating toolbar never leaves a detached More menu behind.
    closingToolbarRef.current = false;
    setMoreOpen(false);
    setColorMenuOpen(false);
    hideLinkPopover();
    suppressAutoFloatRef.current = false;
    setDragPos(null);
    dragPositionRef.current = null;
    setFloatToolbar(true);
    window.requestAnimationFrame(computeFloating);
  };

  const closeFloatingToolbar = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (closingToolbarRef.current) return;
    closingToolbarRef.current = true;

    // Close every menu that belongs to the toolbar before returning it inline.
    setMoreOpen(false);
    setColorMenuOpen(false);
    hideLinkPopover();

    // Cancel a drag without committing its last floating coordinates.
    endDragToolbar(undefined, false);
    pendingPosRef.current = null;
    dragPositionRef.current = null;
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }

    // Snap the element inline immediately. React then confirms the same state.
    const toolbar = toolbarRef.current;
    if (toolbar) {
      toolbar.style.position = "static";
      toolbar.style.width = "100%";
      toolbar.style.left = "auto";
      toolbar.style.top = "auto";
      toolbar.removeAttribute("data-toolbar-dragging");
    }

    // Keep auto-float suppressed until the user scrolls back to the toolbar's
    // original inline location. This prevents the close action from bouncing.
    suppressAutoFloatRef.current = true;
    setFloatToolbar(false);
    setAutoFloat(false);
    setIsFloatingNow(false);
    setDragPos(null);
    finishEditorInteraction({ restore: false });

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        closingToolbarRef.current = false;
        computeFloating();
      });
    });
  };

  const openTableDialog = () => {
    startEditorInteraction();
    restoreSelection();
    setTableRows("3");
    setTableColumns("3");
    setTableHeaderRow(true);
    setTableError("");
    setTableDialogOpen(true);
    window.setTimeout(() => tableRowsInputRef.current?.focus(), 0);
  };

  const closeTableDialog = () => {
    setTableDialogOpen(false);
    setTableError("");
    window.requestAnimationFrame(() => {
      finishEditorInteraction({ restore: true });
      refreshState();
    });
  };

  const insertTable = (event) => {
    event?.preventDefault?.();
    const rows = Number.parseInt(tableRows, 10);
    const cols = Number.parseInt(tableColumns, 10);

    if (!Number.isInteger(rows) || !Number.isInteger(cols) || rows < 1 || cols < 1) {
      setTableError("Enter at least 1 row and 1 column.");
      return;
    }

    const root = editorRef?.current;
    if (!root) {
      setTableError("The editor is not ready for table insertion.");
      return;
    }

    if (typeof insertCanvasTable !== "function") {
      setTableError("The editor table-insertion service is unavailable.");
      return;
    }

    const scrollSnapshot = captureScrollPosition();
    let bridgeReportedSuccess = false;

    // The dialog temporarily owns focus. Restore the saved Canvas range exactly
    // once, then hand the complete request to the Editor. The Toolbar does not
    // construct, inject, clone, rebuild, or fall back to its own table. This keeps
    // manual insertion on one path and avoids the old appear/disappear jitter:
    // Toolbar -> DocCanvasEditor -> DocWriterTable.
    restoreSelection();
    focusEditor();
    restoreSelection();

    beginToolbarMutation();
    try {
      const result = insertCanvasTable({
        rows,
        columns: cols,
        headerRow: tableHeaderRow,
        source: "toolbar",
      });

      bridgeReportedSuccess =
        result === true ||
        Boolean(
          result &&
            typeof result === "object" &&
            (result.changed === true ||
              result.inserted === true ||
              result.table ||
              result.tableId)
        );
    } catch {
      bridgeReportedSuccess = false;
    } finally {
      endToolbarMutation();
    }

    if (!bridgeReportedSuccess) {
      setTableError("The table could not be inserted at the current cursor position.");
      restoreScrollPosition(scrollSnapshot);
      // Keep the interaction open while the dialog remains open so the saved
      // Canvas selection is available for a corrected request or retry.
      return;
    }

    // DocCanvasEditor/DocWriterTable own persistence, pagination, and the table
    // transaction. Do not record a second toolbar history snapshot here.
    setTableDialogOpen(false);
    setTableError("");
    finishEditorInteraction({ restore: false });
    restoreScrollPosition(scrollSnapshot);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        rememberSelection();
        refreshState();
        restoreScrollPosition(scrollSnapshot);
      });
    });
  };

  const isSupportedImageFile = (file) => {
    if (!file) return false;
    const mime = String(file.type || "").trim().toLowerCase();
    if (DOC_WRITER_IMAGE_TYPES.has(mime)) return true;
    const name = String(file.name || "").trim().toLowerCase();
    return /\.(?:jpe?g|png|webp|gif)$/.test(name);
  };

  const openImagePicker = () => {
    // Keep the exact Canvas caret alive while the operating-system file chooser
    // temporarily owns focus. This mirrors Insert Table's selection discipline.
    startEditorInteraction();
    restoreSelection();
    setMoreOpen(false);

    const input = imageInputRef.current;
    if (!input) {
      finishEditorInteraction({ restore: true });
      return;
    }

    // Clearing the input allows selecting the same image twice in succession.
    input.value = "";
    try {
      input.click();
    } catch {
      finishEditorInteraction({ restore: true });
    }
  };

  const handleImageInputCancel = () => {
    window.requestAnimationFrame(() => {
      finishEditorInteraction({ restore: true });
      refreshState();
    });
  };

  const handleImageInputChange = async (event) => {
    const input = event.currentTarget;
    const file = input?.files?.[0] || null;
    if (input) input.value = "";

    if (!file) {
      handleImageInputCancel();
      return;
    }

    if (!isSupportedImageFile(file)) {
      console.warn("Doc Writer image insertion accepts JPEG, PNG, WebP, or GIF files.");
      finishEditorInteraction({ restore: true });
      return;
    }

    if (typeof insertCanvasImage !== "function") {
      // The Toolbar intentionally never inserts image DOM itself. DocCanvasEditor
      // supplies this bridge when image insertion is enabled so there is one
      // authoritative placement/persistence path into DocWriterImage.
      console.warn("The Doc Writer image-insertion bridge is not available yet.");
      finishEditorInteraction({ restore: true });
      return;
    }

    const scrollSnapshot = captureScrollPosition();
    let bridgeReportedSuccess = false;

    restoreSelection();
    focusEditor();
    restoreSelection();

    beginToolbarMutation();
    try {
      const result = await insertCanvasImage({
        file,
        name: String(file.name || ""),
        type: String(file.type || ""),
        size: Number(file.size || 0),
        source: "toolbar",
      });

      bridgeReportedSuccess =
        result === true ||
        Boolean(
          result &&
            typeof result === "object" &&
            (result.changed === true ||
              result.inserted === true ||
              result.image ||
              result.imageId ||
              result.blockId)
        );
    } catch {
      bridgeReportedSuccess = false;
    } finally {
      endToolbarMutation();
    }

    if (!bridgeReportedSuccess) {
      restoreScrollPosition(scrollSnapshot);
      finishEditorInteraction({ restore: true });
      return;
    }

    // DocCanvasEditor/DocWriterImage own object persistence, pagination, and
    // history. Do not create a duplicate toolbar history snapshot here.
    finishEditorInteraction({ restore: false });
    restoreScrollPosition(scrollSnapshot);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        rememberSelection();
        refreshState();
        restoreScrollPosition(scrollSnapshot);
      });
    });
  };

  const refreshState = () => {
    try {
      const liveSelection = selectionIsInsideEditor();
      const activeRange = currentEditorRange();
      const activeBlock = activeRange
        ? closestEditableBlock(activeRange.startContainer)
        : null;
      const computedAlign = activeBlock
        ? window.getComputedStyle(activeBlock).textAlign
        : "left";
      const align =
        computedAlign === "center"
          ? "center"
          : computedAlign === "right" || computedAlign === "end"
          ? "right"
          : "left";

      setFmt((current) => {
        let block = current.block;
        if (activeBlock) {
          const tag = activeBlock.tagName.toLowerCase();
          block = ["p", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote"].includes(tag)
            ? tag
            : current.block;
        }

        return {
          bold: liveSelection ? document.queryCommandState("bold") : current.bold,
          italic: liveSelection ? document.queryCommandState("italic") : current.italic,
          underline: liveSelection ? document.queryCommandState("underline") : current.underline,
          strike: liveSelection ? document.queryCommandState("strikeThrough") : current.strike,
          sup: liveSelection ? document.queryCommandState("superscript") : current.sup,
          sub: liveSelection ? document.queryCommandState("subscript") : current.sub,
          ol: liveSelection ? document.queryCommandState("insertOrderedList") : current.ol,
          ul: liveSelection ? document.queryCommandState("insertUnorderedList") : current.ul,
          align,
          block,
        };
      });
    } catch {}
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      if (selectionIsInsideEditor()) rememberSelection();
      refreshState();
    };
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  // Native typing must keep ownership of native browser Undo. Toolbar/object
  // mutations set latestHistoryKindRef themselves and dispatch synthetic input,
  // while genuine user input switches history preference back to native.
  useEffect(() => {
    const root = editorRef?.current;
    if (!root) return undefined;

    const markNativeHistory = (event) => {
      if (event?.isTrusted && toolbarMutationDepthRef.current === 0) {
        latestHistoryKindRef.current = "native";
      }
    };

    root.addEventListener("beforeinput", markNativeHistory, true);
    root.addEventListener("input", markNativeHistory, true);
    return () => {
      root.removeEventListener("beforeinput", markNativeHistory, true);
      root.removeEventListener("input", markNativeHistory, true);
    };
  }, [editorRef]);

  // Keep link UX updated when content changes
  useEffect(() => {
    const root = editorRef?.current;
    if (!root) return;
    enforceAnchorUX();
    const mo = new MutationObserver((records) => {
      if (records.some((record) => record.type === "childList")) enforceAnchorUX();
    });
    mo.observe(root, { childList: true, subtree: true });
    return () => {
      try { mo.disconnect(); } catch {}
    };
    // eslint-disable-next-line
  }, [editorRef]);

  // ===== Find/Replace helpers =====
  const SEARCH_BLOCK_SELECTOR =
    "p,h1,h2,h3,h4,h5,h6,blockquote,pre,li,td,th,address,section,article,header,footer,aside";
  const SEARCH_EXCLUDE_SELECTOR = [
    '[data-doc-protected-block="true"]',
    '[data-doc-visual-host="true"]',
    '[data-doc-visual-static="true"]',
    '[data-doc-visual-drag-handle="true"]',
    '[data-doc-visual-resize-handle]',
    '[data-doc-table-resize-handle]',
    '[data-doc-table-move-handle]',
    '[data-doc-visual-drop-marker]',
    '[data-doc-canvas-repeat-only="true"]',
    "template",
    "script",
    "style",
  ].join(",");

  const textNodeIsSearchable = (root, node) => {
    if (!root || !node || node.nodeType !== 3 || !node.nodeValue) return false;
    const parent = node.parentElement;
    if (!parent || !root.contains(parent)) return false;
    if (parent.closest?.(SEARCH_EXCLUDE_SELECTOR)) return false;
    if (parent.closest?.('[aria-hidden="true"]')) return false;
    return true;
  };

  const searchBlockForNode = (root, node) => {
    const parent = node?.parentElement;
    if (!parent) return null;
    const block = parent.closest?.(SEARCH_BLOCK_SELECTOR);
    return block && root.contains(block) ? block : null;
  };

  const buildEditorSearchIndex = () => {
    const root = editorRef.current;
    if (!root) return { text: "", entries: [] };

    const documentValue = root.ownerDocument || document;
    const nodeFilter =
      documentValue.defaultView?.NodeFilter ||
      (typeof NodeFilter !== "undefined" ? NodeFilter : null);
    if (!nodeFilter) return { text: "", entries: [] };

    const walker = documentValue.createTreeWalker(
      root,
      nodeFilter.SHOW_TEXT,
      null,
      false
    );
    const entries = [];
    let text = "";
    let previousBlock = null;
    let node = walker.nextNode();

    while (node) {
      if (textNodeIsSearchable(root, node)) {
        const block = searchBlockForNode(root, node);
        if (
          text &&
          previousBlock &&
          block &&
          block !== previousBlock &&
          !previousBlock.contains(block) &&
          !block.contains(previousBlock)
        ) {
          text += "\n";
        }

        const start = text.length;
        text += node.nodeValue;
        entries.push({ node, start, end: text.length });
        previousBlock = block || previousBlock;
      }
      node = walker.nextNode();
    }

    return { text, entries };
  };

  const indexedPosition = (index, offset, preferEnd = false) => {
    const entries = index?.entries || [];
    if (!entries.length) return null;
    const target = Math.max(0, Number(offset || 0));

    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      if (target < entry.start) continue;
      if (target < entry.end || (preferEnd && target === entry.end)) {
        return {
          node: entry.node,
          offset: Math.max(0, Math.min(entry.node.nodeValue.length, target - entry.start)),
        };
      }
    }

    const last = entries[entries.length - 1];
    return {
      node: last.node,
      offset: last.node.nodeValue.length,
    };
  };

  const rangeForSearchOffsets = (index, start, end) => {
    const root = editorRef.current;
    if (!root || !index || end < start) return null;
    const startPosition = indexedPosition(index, start, false);
    const endPosition = indexedPosition(index, end, true);
    if (!startPosition || !endPosition) return null;

    try {
      const range = root.ownerDocument.createRange();
      range.setStart(startPosition.node, startPosition.offset);
      range.setEnd(endPosition.node, endPosition.offset);
      return range;
    } catch {
      return null;
    }
  };

  const selectRangeForSearchOffsets = (index, start, end) => {
    const root = editorRef.current;
    const range = rangeForSearchOffsets(index, start, end);
    if (!root || !range) return false;

    try {
      focusEditor();
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      rememberSelection();

      const target =
        range.startContainer?.nodeType === 1
          ? range.startContainer
          : range.startContainer?.parentElement;
      window.requestAnimationFrame(() => {
        try {
          target?.scrollIntoView?.({ block: "center", inline: "nearest" });
        } catch {}
      });
      return true;
    } catch {
      return false;
    }
  };

  const linearOffsetForRangeStart = (index, range) => {
    if (!index || !range) return 0;
    const node = range.startContainer;
    const entry = index.entries.find((candidate) => candidate.node === node);
    if (entry) {
      return entry.start + Math.max(0, Math.min(node.nodeValue.length, range.startOffset));
    }
    return Math.max(0, Number(lastFindEnd || 0));
  };

  const escapeRegExp = (value) =>
    String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  useEffect(() => {
    lastFindMatchRef.current = null;
    setLastFindEnd(0);
  }, [findQuery, findCaseSensitive, setLastFindEnd]);

  const findNext = () => {
    const query = String(findQuery || "");
    if (!query) return false;

    const index = buildEditorSearchIndex();
    if (!index.text) return false;

    const flags = findCaseSensitive ? "g" : "gi";
    const expression = new RegExp(escapeRegExp(query), flags);
    const from = Math.max(0, Math.min(index.text.length, Number(lastFindEnd || 0)));
    expression.lastIndex = from;
    let match = expression.exec(index.text);
    if (!match && from > 0) {
      expression.lastIndex = 0;
      match = expression.exec(index.text);
    }
    if (!match) return false;

    const startIndex = Number(match.index || 0);
    const endIndex = startIndex + String(match[0] || "").length;
    if (!selectRangeForSearchOffsets(index, startIndex, endIndex)) return false;

    lastFindMatchRef.current = {
      start: startIndex,
      end: endIndex,
      query,
      caseSensitive: Boolean(findCaseSensitive),
    };
    setLastFindEnd(endIndex);
    return true;
  };

  const replaceSelectionOnce = () => {
    const root = editorRef.current;
    if (!root) return false;

    const query = String(findQuery || "");
    if (!query) return false;

    const index = buildEditorSearchIndex();
    let selection = window.getSelection();
    let selectedText = selection?.rangeCount ? selection.toString() : "";
    let exactMatch = new RegExp(
      `^${escapeRegExp(query)}$`,
      findCaseSensitive ? "" : "i"
    ).test(selectedText);

    // Clicking the Find panel can move browser focus away from contentEditable.
    // Recreate the exact last Find range from the same DOM/text index rather than
    // guessing from the current browser selection.
    if (!exactMatch) {
      const saved = lastFindMatchRef.current;
      if (
        saved &&
        saved.query === query &&
        saved.caseSensitive === Boolean(findCaseSensitive) &&
        selectRangeForSearchOffsets(index, saved.start, saved.end)
      ) {
        selection = window.getSelection();
        selectedText = selection?.rangeCount ? selection.toString() : "";
        exactMatch = new RegExp(
          `^${escapeRegExp(query)}$`,
          findCaseSensitive ? "" : "i"
        ).test(selectedText);
      }
    }

    if (!exactMatch || !selection || selection.rangeCount === 0) return findNext();

    const range = selection.getRangeAt(0).cloneRange();
    const startOffset = linearOffsetForRangeStart(index, range);
    const replacement = String(findReplace || "");
    const historyBefore = captureDirectHistoryState();

    beginToolbarMutation();
    try {
      range.deleteContents();
      const textNode = root.ownerDocument.createTextNode(replacement);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch {
      endToolbarMutation();
      return false;
    }
    endToolbarMutation();

    recordDirectHistory(historyBefore);
    notifyEditorInput();
    const nextOffset = startOffset + replacement.length;
    lastFindMatchRef.current = null;
    setLastFindEnd(nextOffset);
    rememberSelection();
    refreshState();
    try { refreshCanvasLayout?.(); } catch {}
    return true;
  };

  const replaceAll = () => {
    const root = editorRef.current;
    const query = String(findQuery || "");
    if (!root || !query) return false;

    const index = buildEditorSearchIndex();
    if (!index.text) return false;

    const flags = findCaseSensitive ? "g" : "gi";
    const expression = new RegExp(escapeRegExp(query), flags);
    const matches = Array.from(index.text.matchAll(expression));
    if (!matches.length) return false;

    const historyBefore = captureDirectHistoryState();
    const replacement = String(findReplace || "");
    let replaced = 0;

    beginToolbarMutation();
    try {
      // Replace from the end so every earlier DOM offset remains valid. This
      // also supports matches split across inline formatting nodes.
      [...matches].reverse().forEach((match) => {
        const start = Number(match.index || 0);
        const end = start + String(match[0] || "").length;
        const range = rangeForSearchOffsets(index, start, end);
        if (!range) return;
        range.deleteContents();
        range.insertNode(root.ownerDocument.createTextNode(replacement));
        replaced += 1;
      });
    } finally {
      endToolbarMutation();
    }

    if (!replaced) return false;
    recordDirectHistory(historyBefore);
    notifyEditorInput();
    lastFindMatchRef.current = null;
    setLastFindEnd(0);
    rememberSelection();
    refreshState();
    try { refreshCanvasLayout?.(); } catch {}
    return true;
  };

  // ===== Scroll parent detection (so desktop inner scrollers also trigger float) =====
  const getScrollParent = (el) => {
    if (!el) return window;
    let node = el.parentElement;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;
      const canScroll =
        /(auto|scroll)/.test(overflowY) || /(auto|scroll)/.test(overflowX);
      if (canScroll) return node;
      node = node.parentElement;
    }
    return window;
  };

  // ===== Floating layout/metrics =====
  const computeFloating = () => {
    const wrapEl = wrapRef?.current;
    const tbEl = toolbarRef.current;
    if (!wrapEl || !tbEl) return;

    const wrapRect = wrapEl.getBoundingClientRect();
    const tbHeight = tbEl.offsetHeight;
    const tbWidth = wrapRect.width;
    const tbLeft = wrapRect.left;

    // Editing state: caret/focus in editor AND not readonly
    const activeEl = document.activeElement;
    const editorActive =
      !readOnly &&
      (activeEl === editorRef.current || (isCaretInEditor?.() ?? false));

    // While the close action is settling, never let a stale floating prop or a
    // selectionchange event re-pin the toolbar.
    if (closingToolbarRef.current) {
      setAutoFloat(false);
      setIsFloatingNow(false);
      return;
    }

    // Once the original inline toolbar is back in view, allow normal auto-float
    // behavior again on a later scroll past the canvas top.
    if (suppressAutoFloatRef.current && wrapRect.top > 0) {
      suppressAutoFloatRef.current = false;
    }

    // Auto-float only when editing AND scrolled past the canvas top.
    let shouldAutoFloat = editorActive && wrapRect.top <= 0;

    if (suppressAutoFloatRef.current) {
      shouldAutoFloat = false;
    }
    setAutoFloat(shouldAutoFloat);

    // Manual float should pin whenever the editor is on screen (even at top)
    const editorVisible = wrapRect.bottom > 24 && wrapRect.top < window.innerHeight - 24;

    const allowFloating = !!(floatToolbar || shouldAutoFloat);
    const withinTop = wrapRect.top <= 0; // passed canvas top
    const withinBottom = wrapRect.bottom > tbHeight + 8; // still inside canvas

    const shouldFloatNow =
      (floatToolbar && editorVisible && withinBottom) ||
      (allowFloating && withinTop && withinBottom);

    setIsFloatingNow(!!shouldFloatNow);

    setToolbarMetrics({
      height: tbHeight,
      width: tbWidth,
      left: Math.max(8, tbLeft),
    });

    if (shouldFloatNow && !dragPos) {
      let baseTop;
      if (wrapRect.top <= 0) {
        baseTop = Math.min(64, Math.max(8, 8 - wrapRect.top));
      } else {
        baseTop = Math.max(8, Math.min(window.innerHeight - tbHeight - 8, wrapRect.top + 8));
      }
      setFloatTop(baseTop);
    }

    // Find panel (fixed & clamped) – only auto-place if user hasn't dragged it
    if (findOpen && !readOnly && !findDragPos) {
      const rightOffset = Math.max(
        8,
        window.innerWidth - (wrapRect.left + wrapRect.width) + 10
      );
      let top;
      if (shouldAutoFloat || (floatToolbar && shouldFloatNow)) {
        top = Math.max(8, (dragPos?.top ?? floatTop) + tbHeight + 12);
      } else {
        top = Math.max(8, wrapRect.top + tbHeight + 12);
      }
      const approxPanelH = 260;
      if (top + approxPanelH > window.innerHeight - 8) {
        top = Math.max(8, window.innerHeight - approxPanelH - 8);
      }
      setFindFixedPos({ top, right: rightOffset });
    }
  };

  // Subscribe to scroll on both window and the nearest scrollable ancestor
  useEffect(() => {
    const measure = () => computeFloating();
    const roTb = new ResizeObserver(measure);
    const tb = toolbarRef.current;
    if (tb) roTb.observe(tb);

    const scroller = getScrollParent(wrapRef?.current);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    if (scroller && scroller !== window) {
      scroller.addEventListener("scroll", measure, { passive: true });
    }
    document.addEventListener("selectionchange", measure);

    const vv = window.visualViewport;
    if (vv) vv.addEventListener("resize", measure);

    measure();

    return () => {
      try { roTb.disconnect(); } catch {}
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
      if (scroller && scroller !== window) {
        scroller.removeEventListener("scroll", measure);
      }
      document.removeEventListener("selectionchange", measure);
      if (vv) vv.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line
  }, [findOpen, readOnly, floatToolbar, dragPos, floatTop]);

  useEffect(() => {
    computeFloating();
    // eslint-disable-next-line
  }, [fmt.bold, fmt.italic, fmt.underline, fmt.align, fmt.block, showToolbar]);

  // ===== Drag to move (toolbar when floating) =====
  const clampToCanvas = (left, top) => {
    const wrapEl = wrapRef?.current;
    const tbEl = toolbarRef.current;
    if (!wrapEl || !tbEl) return { left, top };

    const wrap = wrapEl.getBoundingClientRect();
    const tb = tbEl.getBoundingClientRect();

    const minLeft = wrap.left + 4;
    const maxLeft = wrap.right - tb.width - 4;
    const minTop = 4;
    const maxTop = Math.min(
        window.innerHeight - tb.height - 4,
        wrap.bottom - tb.height - 4
      );

    return {
      left: Math.max(minLeft, Math.min(maxLeft, left)),
      top: Math.max(minTop, Math.min(maxTop, top)),
    };
  };

  const startDragToolbar = (e) => {
    if (isCompactToolbar) return;
    const target = e.target;

    // --- Guard: if the event starts on any non-drag control, bail out ---
    if (
      target.closest("button") ||
      target.closest("input") ||
      target.closest("select") ||
      target.closest("textarea") ||
      target.closest("[data-nodrag]")
    ) {
      return;
    }
    if (!isFloatingNow || closingToolbarRef.current) return;

    const tbEl = toolbarRef.current;
    if (!tbEl) return;
    const tbRect = tbEl.getBoundingClientRect();
    const startX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const startY = "touches" in e ? e.touches[0].clientY : e.clientY;

    e.preventDefault?.();
    setMoreOpen(false);
    setColorMenuOpen(false);
    hideLinkPopover();
    tbEl.setAttribute("data-toolbar-dragging", "true");

    // Pointer capture plus direct style writes make dragging smooth without a
    // React render and listener teardown on every pointer-move frame.
    if ("pointerId" in e && tbEl.setPointerCapture) {
      try { tbEl.setPointerCapture(e.pointerId); } catch {}
    }

    const initialPosition = {
      top: dragPos?.top ?? tbRect.top,
      left: dragPos?.left ?? tbRect.left,
    };
    dragPositionRef.current = initialPosition;
    dragInfoRef.current = {
      startX,
      startY,
      startTop: initialPosition.top,
      startLeft: initialPosition.left,
      dragging: true,
      pointerId: "pointerId" in e ? e.pointerId : null,
    };

    window.addEventListener("pointermove", onDragToolbarMove, { passive: false });
    window.addEventListener("pointerup", endDragToolbar);
    window.addEventListener("pointercancel", endDragToolbar);
  };

  const scheduleDragUpdate = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const next = pendingPosRef.current;
      pendingPosRef.current = null;
      if (!next) return;

      const toolbar = toolbarRef.current;
      if (toolbar && dragInfoRef.current?.dragging) {
        toolbar.style.left = `${next.left}px`;
        toolbar.style.top = `${next.top}px`;
        dragPositionRef.current = next;
      }
    });
  };

  const onDragToolbarMove = (e) => {
    const info = dragInfoRef.current;
    if (!info || !info.dragging) return;
    const x = "touches" in e ? (e.touches[0]?.clientX ?? info.startX) : e.clientX;
    const y = "touches" in e ? (e.touches[0]?.clientY ?? info.startY) : e.clientY;
    const next = clampToCanvas(
      info.startLeft + (x - info.startX),
      info.startTop + (y - info.startY)
    );
    // Throttle via rAF for smoothness
    pendingPosRef.current = next;
    scheduleDragUpdate();
    e.preventDefault();
  };

  const endDragToolbar = (e, commitPosition = true) => {
    const info = dragInfoRef.current;
    if (info) {
      info.dragging = false;
      const tbEl = toolbarRef.current;
      if (tbEl && info.pointerId && tbEl.releasePointerCapture) {
        try { tbEl.releasePointerCapture(info.pointerId); } catch {}
      }
    }

    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }

    const finalPosition = pendingPosRef.current || dragPositionRef.current;
    pendingPosRef.current = null;
    if (commitPosition && finalPosition && !closingToolbarRef.current) {
      setDragPos(finalPosition);
    }

    toolbarRef.current?.removeAttribute("data-toolbar-dragging");
    dragInfoRef.current = null;

    window.removeEventListener("pointermove", onDragToolbarMove);
    window.removeEventListener("pointerup", endDragToolbar);
    window.removeEventListener("pointercancel", endDragToolbar);
  };

  // ===== Draggable Find panel =====
  const startDragFind = (e) => {
    const fr = findRef.current?.getBoundingClientRect();
    if (!fr) return;
    const startX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const startY = "touches" in e ? e.touches[0].clientY : e.clientY;
    findDragInfoRef.current = {
      startX,
      startY,
      startTop: fr.top,
      startLeft: fr.left,
      dragging: true,
    };
    if (!findDragPos) {
      setFindDragPos({ top: fr.top, left: fr.left });
    }
    window.addEventListener("pointermove", onDragFindMove, { passive: false });
    window.addEventListener("pointerup", endDragFind);
    window.addEventListener("touchmove", onDragFindMove, { passive: false });
    window.addEventListener("touchend", endDragFind);
  };

  const clampFind = (left, top) => {
    const wrapEl = wrapRef?.current;
    const fr = findRef.current?.getBoundingClientRect();
    const width = fr?.width ?? 280;
    const height = fr?.height ?? 240;
    const wrap = wrapEl?.getBoundingClientRect?.();
    const minLeft = 4;
    const maxLeft =
      (wrap ? Math.min(window.innerWidth, wrap.right) : window.innerWidth) -
      width -
      4;
    const minTop = 4;
    const maxTop =
      (wrap ? Math.min(window.innerHeight, wrap.bottom) : window.innerHeight) -
      height -
      4;
    return {
      left: Math.max(minLeft, Math.min(maxLeft, left)),
      top: Math.max(minTop, Math.min(maxTop, top)),
    };
  };

  const onDragFindMove = (e) => {
    const info = findDragInfoRef.current;
    if (!info || !info.dragging) return;
    const x = "touches" in e ? (e.touches[0]?.clientX ?? info.startX) : e.clientX;
    const y = "touches" in e ? (e.touches[0]?.clientY ?? info.startY) : e.clientY;
    const next = clampFind(
      info.startLeft + (x - info.startX),
      info.startTop + (y - info.startY)
    );
    setFindDragPos(next);
    e.preventDefault();
  };

  const endDragFind = () => {
    if (findDragInfoRef.current) findDragInfoRef.current.dragging = false;
    window.removeEventListener("pointermove", onDragFindMove);
    window.removeEventListener("pointerup", endDragFind);
    window.removeEventListener("touchmove", onDragFindMove);
    window.removeEventListener("touchend", endDragFind);
  };

  const computeMorePosition = () => {
    const button = moreButtonRef.current;
    if (!button || isCompactToolbar) return;

    const rect = button.getBoundingClientRect();
    const menuWidth = 288;
    const left = Math.max(
      8,
      Math.min(window.innerWidth - menuWidth - 8, rect.right - menuWidth)
    );

    setMorePosition({
      top: Math.min(window.innerHeight - 12, rect.bottom + 8),
      left,
    });
  };

  const closeMoreMenu = ({ restore = true } = {}) => {
    setMoreOpen(false);
    finishEditorInteraction({ restore });
  };

  const toggleMoreMenu = () => {
    if (moreOpen) {
      closeMoreMenu({ restore: true });
      return;
    }

    setColorMenuOpen(false);
    startEditorInteraction();
    setMoreOpen(true);
    window.requestAnimationFrame(computeMorePosition);
  };

  const runMoreAction = (action, { keepInteraction = false } = {}) => {
    restoreSelection();
    action();
    setMoreOpen(false);
    if (!keepInteraction) finishEditorInteraction({ restore: true });
  };

  useEffect(() => {
    if (!moreOpen) return undefined;

    const closeOnOutsidePress = (event) => {
      const target = event.target;
      if (
        moreMenuRef.current?.contains(target) ||
        moreButtonRef.current?.contains(target)
      ) {
        return;
      }
      closeMoreMenu({ restore: true });
    };

    const reposition = () => computeMorePosition();
    computeMorePosition();

    document.addEventListener("pointerdown", closeOnOutsidePress);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePress);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [moreOpen, isCompactToolbar, isFloatingNow, dragPos]);

  // ===== Styles =====
  const s = useMemo(
    () => ({
      bar: {
        display: showToolbar && !readOnly ? "block" : "none",
        background: "#f9fafb",
        borderBottom: "1px solid #e5e7eb",
        boxShadow: isFloatingNow ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
        zIndex: 5000,
        touchAction: isCompactToolbar ? "pan-x pan-y" : "none",
        willChange: "top, left",
      },
      surface: {
        display: isCompactToolbar ? "flex" : "grid",
        gridTemplateColumns: isCompactToolbar
          ? undefined
          : "auto minmax(0, 1fr) auto",
        alignItems: isCompactToolbar ? "center" : "start",
        gap: isCompactToolbar ? 4 : 9,
        padding: isFloatingNow
          ? isCompactToolbar
            ? "6px 42px 6px 6px"
            : "7px 44px 7px 8px"
          : isCompactToolbar
            ? 6
            : 8,
        boxSizing: "border-box",
        width: "100%",
        minWidth: 0,
        overflowX: isCompactToolbar ? "auto" : "hidden",
        overflowY: "hidden",
        scrollbarWidth: isCompactToolbar ? "thin" : undefined,
        WebkitOverflowScrolling: isCompactToolbar ? "touch" : undefined,
        overscrollBehaviorX: isCompactToolbar ? "contain" : undefined,
      },
      fixedGroup: {
        display: "flex",
        alignItems: "center",
        alignSelf: "start",
        gap: isCompactToolbar ? 4 : 6,
        minWidth: 0,
        flex: "0 0 auto",
      },
      scrollArea: {
        minWidth: isCompactToolbar ? "max-content" : 0,
        alignSelf: isCompactToolbar ? "center" : "start",
        flex: isCompactToolbar ? "0 0 auto" : undefined,
        overflowX: isCompactToolbar ? "visible" : "auto",
        overflowY: "hidden",
        padding: isCompactToolbar ? 0 : "0 9px 3px",
        borderLeft: isCompactToolbar ? "none" : "1px solid #D8E0EA",
        borderRight: isCompactToolbar ? "none" : "1px solid #D8E0EA",
        boxSizing: "border-box",
        scrollbarWidth: isCompactToolbar ? "none" : "thin",
        WebkitOverflowScrolling: isCompactToolbar ? undefined : "touch",
        overscrollBehaviorX: isCompactToolbar ? undefined : "contain",
      },
      scrollGroup: {
        display: "flex",
        alignItems: "center",
        gap: isCompactToolbar ? 4 : 6,
        width: "max-content",
        minWidth: isCompactToolbar ? 0 : "100%",
        boxSizing: "border-box",
        paddingRight: isCompactToolbar ? 0 : 14,
        flexWrap: "nowrap",
      },
      divider: {
        width: 1,
        height: 24,
        flex: "0 0 auto",
        background: "#D8E0EA",
        margin: "0 1px",
      },
      btn: (active) => ({
        minWidth: isCompactToolbar ? 32 : 34,
        height: isCompactToolbar ? 32 : 34,
        padding: isCompactToolbar ? "5px 8px" : "6px 10px",
        fontSize: isCompactToolbar ? 12.5 : 13,
        border: active
          ? "1px solid rgba(37,99,235,0.42)"
          : "1px solid #D1D5DB",
        borderRadius: 8,
        background: active ? "#EEF2FF" : "#FFFFFF",
        color: active ? "#1E40AF" : "#111827",
        cursor: "pointer",
        lineHeight: 1,
        flex: "0 0 auto",
        whiteSpace: "nowrap",
        fontWeight: active ? 850 : 700,
      }),
      styleSelect: {
        height: isCompactToolbar ? 32 : 34,
        width: isCompactToolbar ? 108 : 132,
        minWidth: isCompactToolbar ? 108 : 132,
        padding: "0 28px 0 9px",
        border: "1px solid #D1D5DB",
        borderRadius: 8,
        background: "#FFFFFF",
        color: "#111827",
        fontSize: isCompactToolbar ? 12 : 13,
        fontWeight: 750,
        cursor: "pointer",
        outline: "none",
      },
      colorControl: {
        position: "relative",
        width: isCompactToolbar ? 34 : 38,
        height: isCompactToolbar ? 32 : 34,
        flex: "0 0 auto",
        padding: 0,
        border: colorMenuOpen
          ? "1px solid rgba(37,99,235,0.46)"
          : "1px solid #D1D5DB",
        borderRadius: 8,
        background: colorMenuOpen ? "#EEF2FF" : "#FFFFFF",
        overflow: "hidden",
        cursor: "pointer",
        outline: "none",
      },
      colorLetter: {
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingBottom: 4,
        color: "#111827",
        fontWeight: 950,
        pointerEvents: "none",
      },
      colorSwatch: {
        position: "absolute",
        left: 7,
        right: 7,
        bottom: 4,
        height: 3,
        borderRadius: 999,
        background: textColor,
        pointerEvents: "none",
      },
      colorInput: {
        width: 52,
        height: 36,
        padding: 2,
        border: "1px solid #CBD5E1",
        borderRadius: 8,
        background: "#FFFFFF",
        cursor: "pointer",
        boxSizing: "border-box",
      },
      colorMenu: {
        position: "fixed",
        zIndex: 10030,
        width: 236,
        padding: 12,
        border: "1px solid rgba(15,23,42,0.14)",
        borderRadius: 12,
        background: "#FFFFFF",
        boxShadow: "0 18px 45px rgba(15,23,42,0.20)",
        boxSizing: "border-box",
      },
      colorMenuTitle: {
        marginBottom: 10,
        color: "#0F172A",
        fontSize: 13,
        fontWeight: 950,
      },
      colorPickerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "8px 9px",
        border: "1px solid #E2E8F0",
        borderRadius: 9,
        color: "#334155",
        fontSize: 12.5,
        fontWeight: 800,
      },
      colorValue: {
        marginLeft: "auto",
        color: "#64748B",
        fontSize: 11.5,
        fontWeight: 850,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      },
      colorDefaultButton: {
        width: "100%",
        minHeight: 38,
        marginTop: 10,
        padding: "8px 10px",
        border: "1px solid #CBD5E1",
        borderRadius: 9,
        background: "#FFFFFF",
        color: "#111827",
        cursor: "pointer",
        fontSize: 12.5,
        fontWeight: 900,
        textAlign: "center",
      },
      contextBar: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        minWidth: 0,
        padding: "7px 9px",
        borderTop: "1px solid #E2E8F0",
        background: "#F8FAFC",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        boxSizing: "border-box",
      },
      contextLabel: {
        flex: "0 0 auto",
        padding: "4px 7px",
        borderRadius: 7,
        background: "#E0E7FF",
        color: "#3730A3",
        fontSize: 11,
        fontWeight: 950,
        whiteSpace: "nowrap",
      },
      contextButton: (danger = false, active = false) => ({
        flex: "0 0 auto",
        minHeight: 30,
        padding: "5px 8px",
        border: danger
          ? "1px solid #FCA5A5"
          : active
          ? "1px solid rgba(37,99,235,0.42)"
          : "1px solid #CBD5E1",
        borderRadius: 7,
        background: danger ? "#FFF7F7" : active ? "#EEF2FF" : "#FFFFFF",
        color: danger ? "#B91C1C" : active ? "#1E40AF" : "#334155",
        cursor: "pointer",
        fontSize: 11.5,
        fontWeight: 850,
        whiteSpace: "nowrap",
      }),
      contextSelect: {
        flex: "0 0 auto",
        height: 30,
        padding: "0 8px",
        border: "1px solid #CBD5E1",
        borderRadius: 7,
        background: "#FFFFFF",
        color: "#334155",
        fontSize: 11.5,
        fontWeight: 850,
      },
      contextDivider: {
        flex: "0 0 auto",
        width: 1,
        height: 22,
        background: "#CBD5E1",
      },
      visualTextarea: {
        width: "100%",
        minHeight: 150,
        padding: 10,
        border: "1px solid #CBD5E1",
        borderRadius: 9,
        background: "#FFFFFF",
        color: "#0F172A",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: 12.5,
        lineHeight: 1.45,
        resize: "vertical",
        boxSizing: "border-box",
      },
      moreButton: (active) => ({
        height: isCompactToolbar ? 32 : 34,
        padding: isCompactToolbar ? "5px 8px" : "6px 11px",
        border: active
          ? "1px solid rgba(37,99,235,0.42)"
          : "1px solid #D1D5DB",
        borderRadius: 8,
        background: active ? "#EEF2FF" : "#FFFFFF",
        color: active ? "#1E40AF" : "#111827",
        cursor: "pointer",
        fontSize: isCompactToolbar ? 12 : 13,
        fontWeight: 900,
        whiteSpace: "nowrap",
        flex: "0 0 auto",
      }),
      tableBackdrop: {
        position: "fixed",
        inset: 0,
        zIndex: 10020,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "rgba(15,23,42,0.42)",
        boxSizing: "border-box",
      },
      tableDialog: {
        width: "min(420px, 100%)",
        border: "1px solid rgba(15,23,42,0.14)",
        borderRadius: 16,
        background: "#FFFFFF",
        boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
        padding: 18,
        boxSizing: "border-box",
      },
      tableTitle: {
        margin: 0,
        color: "#0F172A",
        fontSize: 18,
        lineHeight: 1.25,
        fontWeight: 950,
      },
      tableCopy: {
        margin: "7px 0 15px",
        color: "#64748B",
        fontSize: 13,
        lineHeight: 1.5,
        fontWeight: 700,
      },
      linkSelectionPreview: {
        margin: "0 0 14px",
        padding: "10px 11px",
        border: "1px solid #DBEAFE",
        borderRadius: 10,
        background: "#F8FAFF",
      },
      linkSelectionLabel: {
        display: "block",
        marginBottom: 4,
        color: "#64748B",
        fontSize: 10.5,
        fontWeight: 950,
        letterSpacing: ".05em",
        textTransform: "uppercase",
      },
      linkSelectionValue: {
        display: "block",
        color: "#0F172A",
        fontSize: 13,
        lineHeight: 1.45,
        fontWeight: 800,
        overflowWrap: "anywhere",
      },
      tableGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 12,
      },
      tableField: {
        display: "grid",
        gap: 6,
      },
      tableLabel: {
        color: "#334155",
        fontSize: 12.5,
        fontWeight: 900,
      },
      tableInput: {
        width: "100%",
        height: 40,
        padding: "0 10px",
        border: "1px solid #CBD5E1",
        borderRadius: 9,
        background: "#FFFFFF",
        color: "#0F172A",
        fontSize: 14,
        fontWeight: 800,
        outline: "none",
        boxSizing: "border-box",
      },
      tableError: {
        margin: "10px 0 0",
        color: "#B91C1C",
        fontSize: 12.5,
        lineHeight: 1.4,
        fontWeight: 850,
      },
      tableActions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: 9,
        marginTop: 17,
      },
      tableCancel: {
        minHeight: 38,
        padding: "8px 13px",
        border: "1px solid #CBD5E1",
        borderRadius: 9,
        background: "#FFFFFF",
        color: "#334155",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 850,
      },
      tableConfirm: {
        minHeight: 38,
        padding: "8px 14px",
        border: "1px solid #1D4ED8",
        borderRadius: 9,
        background: "#2563EB",
        color: "#FFFFFF",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 900,
      },
      moreBackdrop: {
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        background: "rgba(15,23,42,0.34)",
      },
      moreMenu: {
        position: "fixed",
        zIndex: 10000,
        width: isCompactToolbar ? "auto" : 288,
        left: isCompactToolbar ? 12 : morePosition.left,
        right: isCompactToolbar ? 12 : "auto",
        top: isCompactToolbar ? "auto" : morePosition.top,
        bottom: isCompactToolbar ? 12 : "auto",
        maxHeight: isCompactToolbar ? "70vh" : "min(520px, calc(100vh - 24px))",
        overflowY: "auto",
        border: "1px solid rgba(15,23,42,0.14)",
        borderRadius: isCompactToolbar ? 16 : 12,
        background: "#FFFFFF",
        boxShadow: isCompactToolbar
          ? "0 24px 70px rgba(15,23,42,0.26)"
          : "0 16px 38px rgba(15,23,42,0.18)",
      },
      moreHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: isCompactToolbar ? "13px 14px" : "11px 12px",
        borderBottom: "1px solid #E5E7EB",
        fontSize: 13,
        fontWeight: 950,
      },
      moreClose: {
        width: 30,
        height: 30,
        borderRadius: 8,
        border: "1px solid #D1D5DB",
        background: "#FFFFFF",
        cursor: "pointer",
        fontSize: 18,
        lineHeight: 1,
      },
      moreBody: {
        padding: 10,
        display: "grid",
        gap: 10,
      },
      moreSectionTitle: {
        color: "#64748B",
        fontSize: 10.5,
        fontWeight: 950,
        letterSpacing: ".07em",
        textTransform: "uppercase",
        marginBottom: 6,
      },
      moreGrid: {
        display: isCompactToolbar ? "flex" : "grid",
        flexWrap: isCompactToolbar ? "wrap" : "nowrap",
        alignItems: "stretch",
        justifyContent: "flex-start",
        gridTemplateColumns: isCompactToolbar
          ? undefined
          : "repeat(2, minmax(0, 1fr))",
        gap: 7,
      },
      moreItem: (active = false) => ({
        minHeight: 38,
        width: isCompactToolbar ? "auto" : "100%",
        flex: "0 0 auto",
        padding: "8px 10px",
        borderRadius: 9,
        border: active
          ? "1px solid rgba(37,99,235,0.38)"
          : "1px solid #D1D5DB",
        background: active ? "#EEF2FF" : "#FFFFFF",
        color: active ? "#1E40AF" : "#111827",
        cursor: "pointer",
        fontSize: 12.5,
        fontWeight: 800,
        textAlign: "left",
      }),
      // Close (dock) button visible only when floating — bigger hit area & always clickable
      closeDockBtn: {
        position: "absolute",
        top: 4,
        right: 4,
        width: 32,
        height: 32,
        borderRadius: 8,
        border: "1px solid #d1d5db",
        background: "#fff",
        fontWeight: 800,
        cursor: "pointer",
        lineHeight: 1,
        fontSize: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 6000,
      },
      // Find panel
      findPanel: {
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        width: 280,
        zIndex: 10000,
        boxSizing: "border-box",
        position: "fixed",
      },
      findHeader: {
        padding: "8px 12px",
        borderBottom: "1px solid #eee",
        cursor: "move",
        userSelect: "none",
        fontWeight: 600,
        background: "#fafafa",
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
      },
      findBody: {
        padding: 12,
        display: "grid",
        gap: 8,
      },
      input: {
        width: "100%",
        padding: "6px 8px",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        fontSize: 13,
        boxSizing: "border-box",
        display: "block",
      },
      checkboxRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#374151" },
      btnRow: { display: "flex", gap: 6, justifyContent: "flex-end" },
      smallBtn: {
        padding: "6px 10px",
        borderRadius: 8,
        border: "1px solid #d1d5db",
        background: "#fff",
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
        whiteSpace: "nowrap",
        flex: "0 0 auto",
      },
      closeX: {
        position: "absolute",
        top: 8,
        right: 8,
        background: "transparent",
        border: "none",
        fontWeight: 800,
        cursor: "pointer",
        lineHeight: 1,
        fontSize: 16,
      },
      // Link popover
      linkPop: {
        position: "fixed",
        zIndex: 10001,
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        padding: 8,
        maxWidth: Math.min(360, window.innerWidth - 16),
      },
      linkRow: {
        display: "grid",
        gridTemplateColumns: "1fr auto auto auto",
        alignItems: "center",
        gap: 8,
      },
      urlText: {
        fontSize: 12,
        color: "#111827",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "50vw",
      },
      tinyBtn: {
        padding: "6px 10px",
        borderRadius: 6,
        border: "1px solid #d1d5db",
        background: "#fff",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
      },
    }),
    [
      showToolbar,
      readOnly,
      isFloatingNow,
      isCompactToolbar,
      morePosition.left,
      morePosition.top,
      textColor,
      colorMenuOpen,
    ]
  );

  // Positioning style for the toolbar bar
  const toolbarStyle = useMemo(() => {
    if (showToolbar && !readOnly && (floatToolbar || autoFloat) && isFloatingNow) {
      return {
        ...s.bar,
        position: "fixed",
        top: `${dragPos ? dragPos.top : floatTop}px`,
        left: `${dragPos ? dragPos.left : toolbarMetrics.left}px`,
        width: `${toolbarMetrics.width}px`,
      };
    }
    return {
      ...s.bar,
      position: "static",
      width: "100%",
      left: "auto",
      top: "auto",
    };
  }, [
    s.bar,
    showToolbar,
    readOnly,
    floatToolbar,
    autoFloat,
    isFloatingNow,
    toolbarMetrics,
    dragPos,
    floatTop,
  ]);

  const preserveSelectionOnPress = (event) => {
    event.stopPropagation();
    rememberSelection();
    if (event.pointerType === "mouse") event.preventDefault();
  };

  const preventMouseFocus = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const btnProps = (onClick, active = false, title) => ({
    "data-nodrag": "1",
    onPointerDown: preserveSelectionOnPress,
    onMouseDown: preventMouseFocus,
    onClick: (event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
      window.requestAnimationFrame(refreshState);
    },
    style: s.btn(active),
    type: "button",
    title,
    "aria-label": title,
  });

  const moreBtnProps = (
    onClick,
    active = false,
    title,
    { keepInteraction = false } = {}
  ) => ({
    "data-nodrag": "1",
    onPointerDown: preserveSelectionOnPress,
    onMouseDown: preventMouseFocus,
    onClick: (event) => {
      event.preventDefault();
      event.stopPropagation();
      runMoreAction(onClick, { keepInteraction });
      window.requestAnimationFrame(refreshState);
    },
    style: s.moreItem(active),
    type: "button",
    title,
    "aria-label": title,
  });

  // ===== Link popover logic (hover/tap/edit/open/remove + confirm on click/touch) =====
  useEffect(() => {
    const root = editorRef?.current;
    if (!root) return;

    const getClosestAnchor = (el) => {
      let node = el;
      while (node && node !== root) {
        if (node.tagName === "A") return node;
        node = node.parentElement;
      }
      return null;
    };

    const showForAnchor = (a, clientX, clientY) => {
      if (!a) return;
      cancelLinkPopoverHide();
      const rect = a.getBoundingClientRect();
      const url = a.getAttribute("href") || "";
      a.style.cursor = "pointer";
      setLinkPopover({
        show: true,
        x: Math.min(rect.left, clientX || rect.left),
        y: (clientY || rect.top) + rect.height + 6,
        url,
        anchor: a,
      });
    };

    const handleMouseOver = (e) => {
      const a = getClosestAnchor(e.target);
      if (!a) return;
      showForAnchor(a, e.clientX, e.clientY);
    };

    const handleMouseOut = (e) => {
      const to = e.relatedTarget;
      if (linkPopover.show && linkPopover.anchor) {
        const movingIntoAnchor =
          to && linkPopover.anchor && linkPopover.anchor.contains(to);
        const popEl = document.getElementById("doc-link-popover");
        const movingIntoPop = to && popEl && popEl.contains(to);
        if (!movingIntoAnchor && !movingIntoPop) {
          scheduleLinkPopoverHide();
        }
      }
    };

    const handleClick = (e) => {
      const a = getClosestAnchor(e.target);
      if (!a) return;
      e.preventDefault();
      e.stopPropagation();
      showForAnchor(a, e.clientX, e.clientY);
    };

    const preventSelectOnMousedown = (e) => {
      const a = getClosestAnchor(e.target);
      if (a && !readOnly) {
        // Avoid selection stealing hover/confirm in edit mode
        e.preventDefault();
      }
    };

    const handleTouchStart = (e) => {
      const t = e.touches[0];
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const a = getClosestAnchor(el);
      if (a) {
        e.preventDefault();
        showForAnchor(a, t.clientX, t.clientY);
      }
    };

    const handleScrollOrResize = () => {
      if (linkPopover.show && linkPopover.anchor) {
        const rect = linkPopover.anchor.getBoundingClientRect();
        setLinkPopover((p) => ({
          ...p,
          x: rect.left,
          y: rect.top + rect.height + 6,
        }));
      }
    };

    // Capture-phase so we beat contenteditable selection/handlers
    root.addEventListener("mouseover", handleMouseOver, { capture: true });
    root.addEventListener("mouseout", handleMouseOut, { capture: true });
    root.addEventListener("mousedown", preventSelectOnMousedown, { capture: true });
    root.addEventListener("click", handleClick, { capture: true });
    root.addEventListener("touchstart", handleTouchStart, { passive: false, capture: true });
    window.addEventListener("scroll", handleScrollOrResize, { passive: true });
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      root.removeEventListener("mouseover", handleMouseOver, { capture: true });
      root.removeEventListener("mouseout", handleMouseOut, { capture: true });
      root.removeEventListener("mousedown", preventSelectOnMousedown, { capture: true });
      root.removeEventListener("click", handleClick, { capture: true });
      root.removeEventListener("touchstart", handleTouchStart, { capture: true });
      window.removeEventListener("scroll", handleScrollOrResize);
      window.removeEventListener("resize", handleScrollOrResize);
      cancelLinkPopoverHide();
    };
    // eslint-disable-next-line
  }, [editorRef, readOnly, linkPopover.show, linkPopover.anchor]);

  const handleLinkOpen = () => {
    try {
      const url = normalizeUrl(linkPopover.url);
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
      hideLinkPopover();
    } catch {}
  };

  const handleLinkEdit = () => {
    openEditLinkDialog(linkPopover.anchor);
  };

  const handleLinkRemove = () => {
    const anchor = linkPopover.anchor;
    if (!anchor || !anchor.parentNode) return;
    const historyBefore = captureDirectHistoryState();

    try {
      const parent = anchor.parentNode;
      const firstMovedNode = anchor.firstChild;
      while (anchor.firstChild) {
        parent.insertBefore(anchor.firstChild, anchor);
      }
      anchor.remove();
      notifyEditorInput();

      if (firstMovedNode?.isConnected) {
        const range = parent.ownerDocument.createRange();
        range.selectNodeContents(firstMovedNode);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } catch {}

    hideLinkPopover();
    rememberSelection();
    refreshState();
    recordDirectHistory(historyBefore);
  };

  // ===== Render =====
  return (
    <>
      <style>{`
        .doc-canvas-toolbar-root button,
        .doc-canvas-toolbar-root select,
        .doc-canvas-toolbar-more-menu button,
        .doc-canvas-toolbar-dialog button,
        .doc-canvas-toolbar-find button,
        .doc-canvas-toolbar-link-popover button,
        .doc-canvas-toolbar-color-menu button {
          transform-origin: center;
          transition: transform 90ms ease, box-shadow 120ms ease, filter 120ms ease, background-color 120ms ease;
          -webkit-tap-highlight-color: transparent;
        }

        .doc-canvas-toolbar-root button[data-toolbar-pressed="true"],
        .doc-canvas-toolbar-root select[data-toolbar-pressed="true"],
        .doc-canvas-toolbar-more-menu button[data-toolbar-pressed="true"],
        .doc-canvas-toolbar-dialog button[data-toolbar-pressed="true"],
        .doc-canvas-toolbar-find button[data-toolbar-pressed="true"],
        .doc-canvas-toolbar-link-popover button[data-toolbar-pressed="true"],
        .doc-canvas-toolbar-color-menu button[data-toolbar-pressed="true"] {
          transform: translateY(1px) scale(0.97) !important;
          box-shadow: inset 0 1px 3px rgba(15,23,42,0.20), 0 0 0 2px rgba(37,99,235,0.16) !important;
          filter: brightness(0.96);
        }

        .doc-canvas-toolbar-root[data-toolbar-dragging="true"] {
          cursor: grabbing !important;
          user-select: none !important;
          transition: none !important;
        }

        .doc-canvas-toolbar-root[data-toolbar-dragging="true"] * {
          cursor: grabbing !important;
          user-select: none !important;
        }

        .doc-canvas-toolbar-root button[data-toolbar-flash="true"],
        .doc-canvas-toolbar-root select[data-toolbar-flash="true"],
        .doc-canvas-toolbar-more-menu button[data-toolbar-flash="true"],
        .doc-canvas-toolbar-dialog button[data-toolbar-flash="true"],
        .doc-canvas-toolbar-find button[data-toolbar-flash="true"],
        .doc-canvas-toolbar-link-popover button[data-toolbar-flash="true"],
        .doc-canvas-toolbar-color-menu button[data-toolbar-flash="true"] {
          box-shadow: 0 0 0 2px rgba(37,99,235,0.20) !important;
          filter: brightness(0.98);
        }

        @media (prefers-reduced-motion: reduce) {
          .doc-canvas-toolbar-root button,
          .doc-canvas-toolbar-root select,
          .doc-canvas-toolbar-more-menu button,
          .doc-canvas-toolbar-dialog button,
          .doc-canvas-toolbar-find button,
          .doc-canvas-toolbar-link-popover button,
          .doc-canvas-toolbar-color-menu button {
            transition: none !important;
          }
        }
      `}</style>

      {/* Desktop keeps three segments; compact/mobile uses one continuous scroll row. */}
      <div
        ref={toolbarRef}
        className="doc-canvas-toolbar-root"
        style={toolbarStyle}
        aria-label="Editor toolbar"
        onPointerDown={startDragToolbar}
      >
        {/* Close button — returns the floating toolbar to its original position. */}
        {(floatToolbar || autoFloat) && isFloatingNow && (
          <button
            type="button"
            style={s.closeDockBtn}
            title="Close floating toolbar"
            aria-label="Close floating toolbar"
            data-nodrag="1"
            onPointerDown={closeFloatingToolbar}
            onMouseDown={(event) => {
              event.stopPropagation();
              event.preventDefault();
            }}
            onClick={(event) => {
              // Keyboard activation has no pointer-down event.
              if (event.detail === 0) closeFloatingToolbar(event);
            }}
          >
            ×
          </button>
        )}

        <div ref={scrollSurfaceRef} style={s.surface}>
          {/* Desktop fixed left; first controls in the mobile continuous row. */}
          <div style={s.fixedGroup}>
            <button
              {...btnProps(() => runHistoryCommand("undo"), false, "Undo (Ctrl/Cmd+Z)")}
              aria-label="Undo"
            >
              Undo
            </button>
            <button
              {...btnProps(
                () => runHistoryCommand("redo"),
                false,
                "Redo (Ctrl+Y or Cmd+Shift+Z)"
              )}
              aria-label="Redo"
            >
              Redo
            </button>
            <select
              data-nodrag="1"
              aria-label="Text style"
              title="Text style"
              value={
                ["p", "h1", "h2", "h3"].includes(fmt.block)
                  ? fmt.block
                  : "p"
              }
              onPointerDown={(event) => {
                event.stopPropagation();
                startEditorInteraction();
              }}
              onChange={(event) => {
                restoreSelection();
                if (event.target.value === "quote") {
                  addQuotationMarks();
                } else {
                  setBlock(event.target.value);
                }
                window.requestAnimationFrame(() =>
                  finishEditorInteraction({ restore: true })
                );
              }}
              style={s.styleSelect}
            >
              <option value="p">Normal</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
              <option value="quote">Quote</option>
            </select>
          </div>

          {/* Desktop scrollable middle; inline continuation on compact/mobile. */}
          <div
            style={s.scrollArea}
            aria-label="Formatting tools"
          >
            <div style={s.scrollGroup}>
              <button
                {...btnProps(cutSelection, false, "Cut selected content")}
                aria-label="Cut selected content"
              >
                Cut
              </button>
              <button
                {...btnProps(copySelection, false, "Copy selected content")}
                aria-label="Copy selected content"
              >
                Copy
              </button>
              <button
                {...btnProps(pasteClipboard, false, "Paste at the cursor")}
                aria-label="Paste at the cursor"
              >
                Paste
              </button>
              <button
                {...btnProps(
                  selectAllEditorContent,
                  false,
                  "Select all document content"
                )}
                aria-label="Select all document content"
              >
                Select All
              </button>

              <span style={s.divider} aria-hidden="true" />

              <button
                {...btnProps(
                  () => exec("bold"),
                  fmt.bold,
                  "Bold (Ctrl/Cmd+B)"
                )}
                aria-label="Bold"
              >
                <strong>B</strong>
              </button>
              <button
                {...btnProps(
                  () => exec("italic"),
                  fmt.italic,
                  "Italic (Ctrl/Cmd+I)"
                )}
                aria-label="Italic"
              >
                <em>I</em>
              </button>
              <button
                {...btnProps(
                  () => exec("underline"),
                  fmt.underline,
                  "Underline (Ctrl/Cmd+U)"
                )}
                aria-label="Underline"
              >
                <u>U</u>
              </button>

              <button
                ref={colorButtonRef}
                type="button"
                data-nodrag="1"
                style={s.colorControl}
                title="Text color"
                aria-label="Text color"
                aria-haspopup="dialog"
                aria-expanded={colorMenuOpen}
                onPointerDown={preserveSelectionOnPress}
                onMouseDown={preventMouseFocus}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  toggleColorMenu();
                }}
              >
                <span style={s.colorLetter}>A</span>
                <span style={s.colorSwatch} />
              </button>

              <button
                {...btnProps(highlight, false, "Highlight selected text")}
              >
                Highlight
              </button>

              <span style={s.divider} aria-hidden="true" />

              <button
                {...btnProps(
                  () => exec("insertUnorderedList"),
                  fmt.ul,
                  "Bulleted list (Ctrl/Cmd+Shift+8)"
                )}
              >
                • List
              </button>
              <button
                {...btnProps(
                  () => exec("insertOrderedList"),
                  fmt.ol,
                  "Numbered list (Ctrl/Cmd+Shift+7)"
                )}
              >
                1. List
              </button>

              <span style={s.divider} aria-hidden="true" />

              <button
                {...btnProps(
                  () => setAlign("left"),
                  fmt.align === "left",
                  "Align left"
                )}
                aria-label="Align left"
              >
                ≡
              </button>
              <button
                {...btnProps(
                  () => setAlign("center"),
                  fmt.align === "center",
                  "Align center"
                )}
                aria-label="Align center"
              >
                ≣
              </button>
              <button
                {...btnProps(
                  () => setAlign("right"),
                  fmt.align === "right",
                  "Align right"
                )}
                aria-label="Align right"
              >
                ≡
              </button>
              <button
                {...btnProps(() => adjustIndent(-36), false, "Decrease indent")}
              >
                ⇤
              </button>
              <button
                {...btnProps(() => adjustIndent(36), false, "Increase indent")}
              >
                ⇥
              </button>

              <span style={s.divider} aria-hidden="true" />

              <button {...btnProps(openTableDialog, false, "Insert table")}>
                Table
              </button>
              <button
                {...btnProps(
                  openImagePicker,
                  false,
                  "Insert picture (JPEG, PNG, WebP, or GIF)"
                )}
              >
                Image
              </button>
              <button
                {...btnProps(
                  insertPageBreak,
                  false,
                  "Insert Page Break — start the following content on a new page (Ctrl/Cmd+Enter)"
                )}
                aria-keyshortcuts="Control+Enter Meta+Enter"
              >
                {isCompactToolbar ? "Break" : "Page Break"}
              </button>
              <button
                {...btnProps(
                  toggleFindPanel,
                  findOpen,
                  "Find / Replace (Ctrl/Cmd+Shift+F)"
                )}
              >
                Find
              </button>
            </div>
          </div>

          {/* Desktop fixed right; final control in the mobile continuous row. */}
          <div style={s.fixedGroup}>
            <button
              ref={moreButtonRef}
              type="button"
              data-nodrag="1"
              style={s.moreButton(moreOpen)}
              aria-expanded={moreOpen}
              aria-haspopup="menu"
              onPointerDown={preserveSelectionOnPress}
              onMouseDown={preventMouseFocus}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleMoreMenu();
              }}
              title="More formatting tools"
            >
              ••• More
            </button>
          </div>
        </div>
      </div>

      <input
        ref={imageInputRef}
        type="file"
        accept={DOC_WRITER_IMAGE_ACCEPT}
        aria-label="Choose picture to insert"
        title="Choose a JPEG, PNG, WebP, or GIF picture"
        style={{ display: "none" }}
        onChange={handleImageInputChange}
        onCancel={handleImageInputCancel}
      />

      {/* Table-specific controls intentionally do not render in the main toolbar.
          Each DocWriterTable provides its own non-printing Edit button and move/resize UI. */}

      {canvasObjectContext.visualActive ? (
        <div
          style={s.contextBar}
          role="toolbar"
          aria-label="Selected chart or math tools"
          data-nodrag="1"
        >
          <span style={s.contextLabel}>
            {String(canvasObjectContext.visualType || "Visual").replace(/[-_]/g, " ")}
          </span>
          <button
            {...btnProps(() => resizeSelectedVisual(0.9), false, "Make visual smaller")}
            style={s.contextButton()}
          >
            Smaller
          </button>
          <button
            {...btnProps(() => resizeSelectedVisual(1.1), false, "Make visual larger")}
            style={s.contextButton()}
          >
            Larger
          </button>
          <button
            {...btnProps(setSelectedVisualFullWidth, false, "Use full page width")}
            style={s.contextButton()}
          >
            Full width
          </button>
          <span style={s.contextDivider} aria-hidden="true" />
          <button
            {...btnProps(
              () => setSelectedVisualAlignment("left"),
              canvasObjectContext.visualAlign === "left",
              "Align visual left"
            )}
            style={s.contextButton(false, canvasObjectContext.visualAlign === "left")}
          >
            Left
          </button>
          <button
            {...btnProps(
              () => setSelectedVisualAlignment("center"),
              canvasObjectContext.visualAlign === "center",
              "Center visual"
            )}
            style={s.contextButton(false, canvasObjectContext.visualAlign === "center")}
          >
            Center
          </button>
          <button
            {...btnProps(
              () => setSelectedVisualAlignment("right"),
              canvasObjectContext.visualAlign === "right",
              "Align visual right"
            )}
            style={s.contextButton(false, canvasObjectContext.visualAlign === "right")}
          >
            Right
          </button>
          <span style={s.contextDivider} aria-hidden="true" />
          <button
            {...btnProps(openVisualDialog, false, "Edit visual source and size")}
            style={s.contextButton()}
          >
            Edit
          </button>
          <button
            {...btnProps(deleteSelectedVisual, false, "Delete visual")}
            style={s.contextButton(true)}
          >
            Delete
          </button>
        </div>
      ) : null}

      {colorMenuOpen ? (
        <div
          ref={colorMenuRef}
          className="doc-canvas-toolbar-color-menu"
          style={{
            ...s.colorMenu,
            top: colorMenuPosition.top,
            left: colorMenuPosition.left,
          }}
          role="dialog"
          aria-label="Text color options"
          data-nodrag="1"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div style={s.colorMenuTitle}>Text color</div>
          <label style={s.colorPickerRow}>
            <span>Choose color</span>
            <span style={s.colorValue}>{textColor.toUpperCase()}</span>
            <input
              type="color"
              value={textColor}
              onInput={(event) => chooseTextColor(event.currentTarget.value)}
              style={s.colorInput}
              aria-label="Choose custom text color"
            />
          </label>
          <button
            type="button"
            style={s.colorDefaultButton}
            onClick={resetTextColor}
          >
            Default text color (Black)
          </button>
        </div>
      ) : null}

      {tableDialogOpen ? (
        <div
          style={s.tableBackdrop}
          role="presentation"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) closeTableDialog();
          }}
        >
          <form
            className="doc-canvas-toolbar-dialog"
            style={s.tableDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="doc-table-dialog-title"
            onSubmit={insertTable}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <h3 id="doc-table-dialog-title" style={s.tableTitle}>
              Insert table
            </h3>
            <p style={s.tableCopy}>
              Choose the starting rows and columns. The request is handed directly to DocWriterTable through the editor, and the inserted table can be edited, resized, moved, split, merged, or expanded from its own Edit control.
            </p>

            <div style={s.tableGrid}>
              <label style={s.tableField}>
                <span style={s.tableLabel}>Rows</span>
                <input
                  ref={tableRowsInputRef}
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={tableRows}
                  onChange={(event) => {
                    setTableRows(event.target.value);
                    setTableError("");
                  }}
                  style={s.tableInput}
                />
              </label>
              <label style={s.tableField}>
                <span style={s.tableLabel}>Columns</span>
                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={tableColumns}
                  onChange={(event) => {
                    setTableColumns(event.target.value);
                    setTableError("");
                  }}
                  style={s.tableInput}
                />
              </label>
            </div>

            <label style={{ ...s.checkboxRow, marginTop: 12 }}>
              <input
                type="checkbox"
                checked={tableHeaderRow}
                onChange={(event) => setTableHeaderRow(event.target.checked)}
              />
              Use the first row as a header
            </label>

            {tableError ? <p style={s.tableError}>{tableError}</p> : null}

            <div style={s.tableActions}>
              <button type="button" style={s.tableCancel} onClick={closeTableDialog}>
                Cancel
              </button>
              <button type="submit" style={s.tableConfirm}>
                Insert table
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {visualDialogOpen ? (
        <div
          style={s.tableBackdrop}
          role="presentation"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) closeVisualDialog();
          }}
        >
          <form
            className="doc-canvas-toolbar-dialog"
            style={s.tableDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="doc-visual-dialog-title"
            onSubmit={submitVisualDialog}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <h3 id="doc-visual-dialog-title" style={s.tableTitle}>
              Edit {String(canvasObjectContext.visualType || "visual").replace(/[-_]/g, " ")}
            </h3>
            <p style={s.tableCopy}>
              Update the saved chart or math instruction and its canvas size.
            </p>

            <label style={s.tableField}>
              <span style={s.tableLabel}>Source / specification</span>
              <textarea
                value={visualSource}
                onChange={(event) => {
                  setVisualSource(event.target.value);
                  setVisualError("");
                }}
                style={s.visualTextarea}
                placeholder="Chart or math source instruction"
              />
            </label>

            <div style={{ ...s.tableGrid, marginTop: 12 }}>
              <label style={s.tableField}>
                <span style={s.tableLabel}>Width (px)</span>
                <input
                  type="number"
                  min="140"
                  max="1600"
                  value={visualWidthInput}
                  onChange={(event) => {
                    setVisualWidthInput(event.target.value);
                    setVisualError("");
                  }}
                  style={s.tableInput}
                />
              </label>
              <label style={s.tableField}>
                <span style={s.tableLabel}>Height (px)</span>
                <input
                  type="number"
                  min="80"
                  max="1200"
                  value={visualHeightInput}
                  onChange={(event) => {
                    setVisualHeightInput(event.target.value);
                    setVisualError("");
                  }}
                  style={s.tableInput}
                />
              </label>
            </div>

            {visualError ? <p style={s.tableError}>{visualError}</p> : null}

            <div style={s.tableActions}>
              <button type="button" style={s.tableCancel} onClick={closeVisualDialog}>
                Cancel
              </button>
              <button type="submit" style={s.tableConfirm}>
                Save visual
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {linkDialogOpen ? (
        <div
          style={s.tableBackdrop}
          role="presentation"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) closeLinkDialog();
          }}
        >
          <form
            className="doc-canvas-toolbar-dialog"
            style={s.tableDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="doc-link-dialog-title"
            onSubmit={submitLinkDialog}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <h3 id="doc-link-dialog-title" style={s.tableTitle}>
              {linkDialogMode === "edit" ? "Edit link" : "Add link"}
            </h3>
            <p style={s.tableCopy}>
              Enter a web address. Selected words stay visible and become the clickable link.
            </p>

            {linkSelectedText ? (
              <div style={s.linkSelectionPreview}>
                <span style={s.linkSelectionLabel}>Selected text</span>
                <span style={s.linkSelectionValue}>{linkSelectedText}</span>
              </div>
            ) : null}

            <label style={s.tableField}>
              <span style={s.tableLabel}>Link URL</span>
              <input
                ref={linkInputRef}
                type="text"
                inputMode="url"
                autoComplete="off"
                value={linkUrl}
                onChange={(event) => {
                  setLinkUrl(event.target.value);
                  setLinkError("");
                }}
                placeholder="www.example.com or https://example.com"
                style={s.tableInput}
              />
            </label>

            {linkError ? <p style={s.tableError}>{linkError}</p> : null}

            <div style={s.tableActions}>
              <button type="button" style={s.tableCancel} onClick={closeLinkDialog}>
                Cancel
              </button>
              <button type="submit" style={s.tableConfirm}>
                {linkDialogMode === "edit" ? "Save link" : "Add link"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {moreOpen && isCompactToolbar ? (
        <div
          style={s.moreBackdrop}
          onPointerDown={() => closeMoreMenu({ restore: true })}
          aria-hidden="true"
        />
      ) : null}

      {moreOpen ? (
        <div
          ref={moreMenuRef}
          className="doc-canvas-toolbar-more-menu"
          style={s.moreMenu}
          role="menu"
          aria-label="More formatting tools"
          data-nodrag="1"
        >
          <div style={s.moreHeader}>
            <span>More formatting tools</span>
            <button
              type="button"
              style={s.moreClose}
              onClick={() => closeMoreMenu({ restore: true })}
              aria-label="Close More tools"
              title="Close"
            >
              ×
            </button>
          </div>

          <div style={s.moreBody}>
            <section>
              <div style={s.moreSectionTitle}>Text</div>
              <div style={s.moreGrid}>
                <button
                  {...moreBtnProps(
                    toggleStrike,
                    fmt.strike,
                    "Strikethrough"
                  )}
                >
                  Strikethrough
                </button>
                <button
                  {...moreBtnProps(toggleSup, fmt.sup, "Superscript")}
                >
                  Superscript
                </button>
                <button
                  {...moreBtnProps(toggleSub, fmt.sub, "Subscript")}
                >
                  Subscript
                </button>
                <button
                  {...moreBtnProps(
                    () => exec("removeFormat"),
                    false,
                    "Clear formatting"
                  )}
                >
                  Clear formatting
                </button>
              </div>
            </section>

            <section>
              <div style={s.moreSectionTitle}>Additional styles</div>
              <div style={s.moreGrid}>
                <button
                  {...moreBtnProps(
                    () => setBlock("h4"),
                    fmt.block === "h4",
                    "Heading 4"
                  )}
                >
                  Heading 4
                </button>
                <button
                  {...moreBtnProps(
                    () => setBlock("h5"),
                    fmt.block === "h5",
                    "Heading 5"
                  )}
                >
                  Heading 5
                </button>
                <button
                  {...moreBtnProps(
                    () => setBlock("h6"),
                    fmt.block === "h6",
                    "Heading 6"
                  )}
                >
                  Heading 6
                </button>
                <button
                  {...moreBtnProps(
                    addQuotationMarks,
                    false,
                    "Add quotation marks"
                  )}
                >
                  Quote
                </button>
              </div>
            </section>

            <section>
              <div style={s.moreSectionTitle}>Links and toolbar</div>
              <div style={s.moreGrid}>
                <button
                  {...moreBtnProps(
                    openCreateLinkDialog,
                    false,
                    "Add link",
                    { keepInteraction: true }
                  )}
                >
                  Add link
                </button>
                <button {...moreBtnProps(unlink, false, "Remove link")}>
                  Remove link
                </button>
                <button
                  {...moreBtnProps(
                    floatToolbarOnly,
                    floatToolbar || autoFloat,
                    "Float toolbar"
                  )}
                >
                  Float toolbar
                </button>
              </div>
            </section>
          </div>
        </div>
      ) : null}

      {/* Find/Replace (draggable) */}
      <div
        ref={findRef}
        className="doc-canvas-toolbar-find"
        style={{
          ...s.findPanel,
          display: findOpen && !readOnly ? "block" : "none",
          top: (findDragPos?.top ?? (findFixedPos?.top ?? (floatTop + (toolbarMetrics?.height || 44) + 12))),
          left: (findDragPos?.left ??
            Math.max(
              8,
              (window.innerWidth - 8) - 280 - (findFixedPos?.right ?? 8)
            )),
        }}
        aria-label="Find and Replace"
      >
        <button
          type="button"
          style={s.closeX}
          data-nodrag="1"
          onClick={() => setFindOpen(false)}
          aria-label="Close find"
          title="Close"
        >
          ×
        </button>

        <div
          style={s.findHeader}
          onPointerDown={startDragFind}
          onTouchStart={startDragFind}
        >
          Find & Replace
        </div>

        <div style={s.findBody}>
          <input
            style={s.input}
            value={findQuery}
            onChange={(e) => setFindQuery(e.target.value)}
            placeholder="Find…"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                findNext();
              }
            }}
          />
          <input
            style={s.input}
            value={findReplace}
            onChange={(e) => setFindReplace(e.target.value)}
            placeholder="Replace with…"
          />
          <label style={s.checkboxRow}>
            <input
              type="checkbox"
              checked={findCaseSensitive}
              onChange={(e) => setFindCaseSensitive(e.target.checked)}
            />
            Case sensitive
          </label>
          <div style={s.btnRow}>
            <button style={s.smallBtn} data-nodrag="1" onMouseDown={preventMouseFocus} onClick={findNext}>Find Next</button>
            <button style={s.smallBtn} data-nodrag="1" onMouseDown={preventMouseFocus} onClick={replaceSelectionOnce}>Replace</button>
            <button style={s.smallBtn} data-nodrag="1" onMouseDown={preventMouseFocus} onClick={replaceAll}>Replace All</button>
          </div>
        </div>
      </div>

      {/* Link Popover */}
      {linkPopover.show && (
        <div
          id="doc-link-popover"
          className="doc-canvas-toolbar-link-popover"
          style={{ ...s.linkPop, left: linkPopover.x, top: linkPopover.y }}
          onPointerEnter={cancelLinkPopoverHide}
          onPointerLeave={() => scheduleLinkPopoverHide(180)}
          onPointerDown={(event) => {
            cancelLinkPopoverHide();
            event.stopPropagation();
          }}
          data-nodrag="1"
        >
          <div style={s.linkRow}>
            <div style={s.urlText} title={linkPopover.url}>{linkPopover.url}</div>
            <button style={s.tinyBtn} data-nodrag="1" onClick={handleLinkOpen}>Open</button>
            <button style={s.tinyBtn} data-nodrag="1" onClick={handleLinkEdit}>Edit</button>
            <button style={s.tinyBtn} data-nodrag="1" onClick={handleLinkRemove}>Remove</button>
          </div>
        </div>
      )}
    </>
  );
};

export default DocCanvasToolbar;
